import { Component, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef, inject, NgZone } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { DialogService } from '../shared/components/dialog/dialog.service';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { RecordButtonComponent, RecordControlAction } from '../shared/components/record-button/record-button.component';
import { openCopilotSnackbar } from '../shared/components/copilot-snackbar/open-copilot-snackbar';
import { Editor } from '@tiptap/core';
import { history as createHistoryPlugin } from '@tiptap/pm/history';
import { CLIPBOARD_BLOCK_SEPARATOR, createEditorExtensions, EDITOR_HISTORY_OPTIONS } from './helpers/editor-extensions';
import {
  AiChangeDecorationRange,
  clearAiChangeDecoration,
  setAiChangeDecorations,
} from './helpers/ai-change-decoration.extension';
import {
  computeDiffHighlightRanges,
  extractDocText,
} from './helpers/diff-highlighter';
import { EncounterListItem } from '../_interfaces/encounter-list-item';
import { Dictation, SessionType } from '../_interfaces/dictation';
import {
  applyLiveTranscriptResult,
  getDictationTranscription,
  mapUtterancesToTranscript,
  TranscriptUtterance,
} from '../_helpers/transcription';
import { ApiService } from '../_services/api.service';
import { ConfirmComponent, ConfirmDialogData } from '../shared/dialogs/confirm/confirm.component';
import {
  DetailsAndStyleComponent,
  DetailsAndStyleDialogData,
  DetailsAndStyleResult,
} from '../shared/dialogs/details-and-style/details-and-style.component';
import { hasNoteGenerationSettings } from '../_helpers/note-generation';
import { DictationRetention, getDictationRetention } from '../_helpers/retention';
import { NoteGenerationSettings } from '../_interfaces/note-generation.interface';
import { RegenerateErrorComponent } from './components/regenerate-error/regenerate-error.component';
import { DocumentSyncToolbarComponent } from './components/document-sync-toolbar/document-sync-toolbar.component';
import { ResumeConsultationButtonComponent } from './components/resume-consultation-button/resume-consultation-button.component';
import { isResumeEligible, RESUME_CONSULTATION_FLAG } from './helpers/resume-consultation';
import { RegenerateService } from './services/regenerate/regenerate.service';
import { DocumentFreshnessService } from './services/document-freshness.service';
import { DocumentSyncService } from './services/document-sync.service';
import { Subject, firstValueFrom, timer, race, merge, fromEvent, EMPTY } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';
import { SelectTemplateComponent } from '../shared/dialogs/select-template/select-template.component';
import { CopilotDocument, CopilotRecipients } from '../_interfaces/document';
import { CopilotProcessingService, SmartDictationStaleError } from '../_services/copilot-processing.service';
import { FrontendPhrase, FrontendResult } from '../_interfaces/frontend-result';
import { ApproveWorkflowDialogComponent } from '../shared/dialogs/approve-workflow/approve-workflow.component';
import { TextService } from '../_services/text.service';
import { EditPatientComponent } from '../_dialogs/edit-patient/edit-patient.component';
import { PHRASES } from '../_helpers/phrases';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FeatureFlagsService } from '../_services/feature-flags.service';
import { NgIf } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { RecordingState } from '../_enums/recording-state.enum';
import { calculateAgeYears, sortByTemplateTitle, formatDictationSubjectName } from '../_helpers/utils';
import { formatDmyDate } from '../_helpers/dmy-date';
import { Template } from '../_interfaces/template';
import { LoadingSpinnerComponent } from '../shared/components/loading-spinner/loading-spinner.component';
import { LoadingComponent } from '../shared/components/loading/loading.component';
import { User } from '../_interfaces/user';
import { AuthService } from '../_services/auth.service';
import { ManualPatientSelectorComponent } from '../_dialogs/manual-patient-selector/manual-patient-selector.component';
import { WaveIndicatorComponent } from '../shared/components/wave-indicator/wave-indicator.component';
import { ContentPanelComponent, CONTENT_PANEL_COLLAPSED_HEIGHT_PX } from './components/content-panel/content-panel.component';
import { RecorderService } from '../_services/recorder.service';
import { AnalyticsService, toAnalyticsEncounterType } from '../_services/analytics.service';
import { ScreenWakeLockDirective, WakeLockUnavailableReason } from '../shared/directives/screen-wake-lock.directive';
import { RecipientFieldsComponent } from './components/recipients/recipient-fields.component';

import {
  ccRecipients,
  cloneRecipients,
  generateRecipientSalutation,
  hasPrimaryRecipient,
  isManualRecipient,
  isRecipientManagementEnabled,
  nullAbsentRecipientFields,
  recipientKey,
  withoutManualRecipients,
} from './components/recipients/recipient.helpers';
import {
  fillEmptyParagraphsWithInvisibleCharacter,
  parseMarkdownToHtml,
  serializeNoteToMarkdown,
} from '../_helpers/markdown-to-html';
import { nextContentUpdatedAt } from '../_helpers/content-updated-at';
import { RecipientDialogComponent } from './components/recipients/recipient-dialog.component';
import { TooltipDirective } from '../shared/components/tooltip/tooltip.directive';

export const SMART_DICTATION_FLAG = 'smartDictation';

@Component({
  selector: 'app-document',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatTabsModule,
    RecordButtonComponent,
    MatProgressBarModule,
    MatDividerModule,
    MatFormFieldModule,
    MatSelectModule,
    TranslateModule,
    NgIf,
    ReactiveFormsModule,
    LoadingSpinnerComponent,
    LoadingComponent,
    WaveIndicatorComponent,
    RegenerateErrorComponent,
    DocumentSyncToolbarComponent,
    ResumeConsultationButtonComponent,
    ContentPanelComponent,
    RecipientFieldsComponent,
    ScreenWakeLockDirective,
    TooltipDirective,
  ],
  templateUrl: './document.component.html',
  styleUrls: ['./document.component.scss'],
  providers: [RegenerateService],
  host: {
    class: 'flex flex-col h-screen overflow-hidden'
  }
})
export class DocumentComponent implements OnInit, OnDestroy {
  private _editorContainer?: ElementRef;
  @ViewChild('editorContainer', { static: false })
  set editorContainer(el: ElementRef | undefined) {
    this._editorContainer = el;
    if (!el) {
      return;
    }
    if (!this.editor) {
      setTimeout(() => this.initializeEditor(), 0);
      return;
    }
    const viewDom = this.editor.view?.dom;
    if (viewDom && el.nativeElement.contains(viewDom)) {
      return;
    }
    if (viewDom) {
      el.nativeElement.appendChild(viewDom);
      (this.editor as any).options.element = el.nativeElement;
      return;
    }
    this.editor.destroy();
    this.editor = null;
    setTimeout(() => this.initializeEditor(), 0);
  }
  get editorContainer(): ElementRef | undefined {
    return this._editorContainer;
  }

  loadingDictation = false;
  editor: Editor | null = null;
  dictation: Dictation | null = null;
  encounter: EncounterListItem | null = null;
  selectedTemplateIndex = 0;
  processedDocuments: CopilotDocument[] = [];
  hasDocuments = false;
  isRegenerating = false;
  isSaving = false;
  rightPaneOpen = false;
  private savingCount = 0;
  private currentJobs: any[] = [];
  private editorUpdate$ = new Subject<{ html: string; templateId: number; templateType: string }>();
  private editorAutosaveBound = false;
  private destroy$ = new Subject<void>();
  private programmaticHtmlToIgnore: string | null = null;
  private pendingUpdate: { html: string; templateId: number; templateType: string } | null = null;
  private lastSavedContent: Map<string, string> = new Map();
  showTestCommands = false;
  showToolsPanel = false;
  readonly contentPanelCollapsedHeightPx = CONTENT_PANEL_COLLAPSED_HEIGHT_PX;
  transcriptUtterances: TranscriptUtterance[] = [];
  selectedCommand: string = '';
  user: User;
  isLegalOrg = false;
  isTranscriptionStreaming = false;
  // Tracks whether `onIncomingTranscript` is still expecting the trailing final
  // chunk after recording pauses, so that last utterance isn't dropped once the
  // state has left Recording. Owned solely by `onIncomingTranscript` to avoid a
  // race with the `frontendResult$` subscription, which fires first for the same
  // event and would otherwise have already cleared a shared flag.
  private waitingForFinalTranscript = false;
  private activeTranscriptUtteranceIndex: number | null = null;
  recordingState: RecordingState = RecordingState.Disconnected;

  get screenWakeLockActive(): boolean {
    return this.recordingState === RecordingState.Recording;
  }

  onWakeLockUnavailable(reason: WakeLockUnavailableReason): void {
    this.analytics.track('screen_wake_lock_unavailable', { reason });
  }

  hasMicrophoneAccess = false;
  fromEncounters = false;
  encountersPage: number | null = null;
  private activeRecordingDocumentKey: string | null = null;
  isDictationMode = false;
  isSmartProcessing = false;
  /** Document that was being recorded when smart pause generation started. */
  private smartProcessingDocumentKey: string | null = null;
  /** In-flight pause generation. Tab flush skips this document; approve awaits it. */
  private smartProcessingTask: Promise<void> | null = null;
  /** Editor/pending HTML for the recorded document, captured before a tab switch can replace it. */
  private smartCurrentNoteHtml: string | null = null;
  /** True from the moment recording starts until pause/disconnect, including the Loading gap in `RecorderService.pause()`. */
  private smartPauseArmed = false;
  /** Timer handle for clearing smart dictation change highlight decorations. */
  private smartHighlightTimer: ReturnType<typeof setTimeout> | null = null;
  /** ASR received since the last successful smart POST. Sent as `content` (the update), not the full session transcript. */
  private pendingSmartTranscript = '';
  private smartAbortController: AbortController | null = null;
  private smartGenerationId = 0;
  private readonly smartPauseSeqByKey = new Map<string, number>();
  private readonly smartNoteRevisionByKey = new Map<string, number>();
  private isVoiceSelectionModeActive = false;
  private voiceSelectionSearchTerm = '';
  private templates: Template[] | null = null;
  private updateJobsInFlight = false;
  private activeUpdateJobCount = 0;

  private readonly analytics = inject(AnalyticsService);
  private readonly ngZone = inject(NgZone);
  private readonly recipientDisabledViewed = new Set<string>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private apiService: ApiService,
    private dialog: DialogService,
    private processing: CopilotProcessingService,
    private textService: TextService,
    public flags: FeatureFlagsService,
    private authService: AuthService,
    private recorderService: RecorderService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    public regenerate: RegenerateService,
    private freshness: DocumentFreshnessService,
    private documentSync: DocumentSyncService,
  ) {
    this.user = this.authService.getUser() as User;
    this.isLegalOrg = this.user?.entity?.settings?.category === 'Legal';
  }

  private readonly commandMap: Record<string, string> = {
    '[new-line]': '\n',
    '[new-paragraph]': '\n\n',
  };

  checkForCommand(word: string): string {
    const commandPattern = /\[new-line\]|\[new-paragraph\]/g;
    return word.replace(commandPattern, (match) => this.commandMap[match] ?? match);
  }

  // Receive transcript chunks from floating recorder and append to transcript/context
  async onIncomingTranscript(result: FrontendResult): Promise<void> {
    this.editor?.commands.focus();
    // Ignore null results (e.g., from WebSocket disconnect cleanup)
    if (!result || result.type !== 'result') return;

    // The NLP server fans `frontend-result` events to every connection sharing
    // the same dictation.audio token. If another tab is recording the same
    // dictation, this tab can receive its transcripts even when paused or
    // stopped. Drop them unless this tab is actively recording.
    // Exception: keep accepting through the pause Loading gap (stop still in
    // flight), the trailing-final wait, and in-flight smart processing.
    if (this.isSmartDictationActive) {
      if (!this.isSmartTranscriptWindowOpen) return;
      this.waitingForFinalTranscript = !result.final;

      const chunk = Array.isArray(result.words) ? (result.words[0] ?? '').trim() : '';
      if (
        result.final
        && chunk
        && this.activeTranscriptUtteranceIndex === null
        && this.isDuplicateFinalTranscript(chunk)
      ) {
        this.appendPendingSmartTranscript(chunk);
        return;
      }

      const { utterances, activeIndex, finalChunk } = applyLiveTranscriptResult(
        { utterances: this.transcriptUtterances, activeIndex: this.activeTranscriptUtteranceIndex },
        result,
      );
      this.transcriptUtterances = utterances;
      this.activeTranscriptUtteranceIndex = activeIndex;
      this.cdr.detectChanges();

      // Pin to the recording session, not the selected tab: a tab switch during
      // the pause wait clears activeRecordingDocumentKey and changes selectedDocument.
      if (finalChunk) {
        this.appendPendingSmartTranscript(finalChunk);
      }
      return;
    }

    if (this.recordingState !== RecordingState.Recording && !this.waitingForFinalTranscript) {
      return;
    }
    this.waitingForFinalTranscript = !result.final;

    const selectedDoc = this.selectedDocument;
    if (!selectedDoc) return;
    if (selectedDoc.approved) {
      this.isTranscriptionStreaming = false;
      return;
    }
    const selectedKey = this.getDocumentKey(selectedDoc);
    if (!this.activeRecordingDocumentKey || selectedKey !== this.activeRecordingDocumentKey) {
      this.isTranscriptionStreaming = false;
      return;
    }

    console.log('Received transcript chunk:', result);

    // Process final results
    if (result.final) {
      const hasPhrases = result.phrases && result.phrases.length > 0;
      const hasWords = Array.isArray(result.words) && result.words.length > 0;

      if (hasWords) {
        // ALWAYS process text and look for brackets [...]
        let fullText = result.words[0] || '';
        console.log('Processing text:', fullText, 'Phrases:', result.phrases);

        // Find all bracketed content in the text [...]
        const bracketRegex = /\[([^\]]+)\]/g;
        const bracketMatches: { index: number; length: number; content: string }[] = [];
        let match;

        while ((match = bracketRegex.exec(fullText)) !== null) {
          bracketMatches.push({
            index: match.index,
            length: match[0].length,  // Full length including brackets
            content: match[1]          // Content inside brackets (without brackets)
          });
        }
        console.log('Found brackets in text:', bracketMatches);

        // If we received command phrases but no inline bracket tokens,
        // treat this chunk as command-only and avoid inserting transcript words.
        if (hasPhrases && bracketMatches.length === 0) {
          for (const phrase of result.phrases!) {
            await this.applyCommand(phrase);
          }
          return;
        }

        // Process text and commands in order
        let lastIndex = 0;
        for (const bracketMatch of bracketMatches) {
          // Insert text before the bracket (NOT including the bracket)
          const textBefore = fullText.substring(lastIndex, bracketMatch.index).trim();
          if (textBefore) {
            console.log('Inserting text:', textBefore);
            await this.insertTranscriptText(textBefore);
          }

          // Find matching phrase in phrases array OR create one from bracket content
          if (hasPhrases) {
            // Scenario 1: phrases field exists (entire sentence is a command)
            const matchingPhrase = result.phrases!.find(p => {
              // Try multiple normalization strategies for flexible matching
              const phraseName = p.name.toLowerCase().trim();
              const bracketContent = bracketMatch.content.toLowerCase().trim();

              // Strategy 1: Exact match
              if (phraseName === bracketContent) return true;

              // Strategy 2: Both normalized (hyphens/underscores to spaces)
              const nameNormalized = phraseName.replace(/[-_]/g, ' ').replace(/\s+/g, ' ');
              const contentNormalized = bracketContent.replace(/[-_]/g, ' ').replace(/\s+/g, ' ');
              if (nameNormalized === contentNormalized) return true;

              // Strategy 3: Remove all spaces/hyphens from both
              const nameCompact = phraseName.replace(/[-_\s]/g, '');
              const contentCompact = bracketContent.replace(/[-_\s]/g, '');
              if (nameCompact === contentCompact) return true;

              return false;
            });

            if (matchingPhrase) {
              console.log('✓ Executing command from phrases:', matchingPhrase.name);
              await this.applyCommand(matchingPhrase);
            } else {
              console.log('✗ No matching phrase for bracket content:', bracketMatch.content);
            }
          } else {
            // Scenario 2: No phrases field (command in middle of sentence)
            // Create command directly from bracket content
            const command: FrontendPhrase = {
              name: bracketMatch.content,
              parameters: []
            };
            console.log('✓ Executing command from bracket:', command.name);
            await this.applyCommand(command);
          }

          // Move past the entire bracket [...]
          lastIndex = bracketMatch.index + bracketMatch.length;
        }

        // Insert remaining text after the last bracket
        const textAfter = fullText.substring(lastIndex).trim();
        if (textAfter) {
          console.log('Inserting remaining text:', textAfter);
          await this.insertTranscriptText(textAfter);
        }
      } else if (hasPhrases) {
        // Only commands, no text - execute all phrases
        for (const phrase of result.phrases!) {
          await this.applyCommand(phrase);
        }
      }
    }
  }

  async applyCommand(command: FrontendPhrase): Promise<void> {
    console.log('Applying command:', command.name, command.parameters);
    const normalizedCommand = this.normalizeCommandName(command.name);
    const isVoiceSelectionFollowUpCommand = this.isVoiceSelectionFollowUpCommand(normalizedCommand);
    const shouldKeepVoiceSelectionForCommand = this.isVoiceSelectionWorkflowCommand(normalizedCommand) || isVoiceSelectionFollowUpCommand;
    if (this.isVoiceSelectionModeActive && !shouldKeepVoiceSelectionForCommand) {
      await this.exitVoiceSelectionMode();
    }
    if (normalizedCommand === 'select-<text>') {
      await this.enterVoiceSelectionMode(command);
    }
    await this.textService.applyCommand(this.editor, command);
    this.editor?.commands.scrollIntoView();
    if (isVoiceSelectionFollowUpCommand) {
      await this.exitVoiceSelectionMode();
    }
    if (normalizedCommand === 'unselect-text') {
      await this.exitVoiceSelectionMode(false);
    }
    this.updateEditorDisabledState();
  }

  get isSmartDictationActive(): boolean {
    return this.flags.isEnabled(SMART_DICTATION_FLAG) && this.isDictationMode;
  }

  /**
   * RecorderService.pause() emits Loading while stop is in flight. A new utterance
   * whose previous result was already final would otherwise be dropped there.
   */
  private get isSmartTranscriptWindowOpen(): boolean {
    return this.recordingState === RecordingState.Recording
      || this.smartPauseArmed
      || this.waitingForFinalTranscript
      || this.isSmartProcessing;
  }

  private isDuplicateFinalTranscript(chunk: string): boolean {
    const last = this.transcriptUtterances[this.transcriptUtterances.length - 1];
    return last?.final === true && last.text === chunk;
  }

  private appendPendingSmartTranscript(text: string): void {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (this.pendingSmartTranscript.endsWith(trimmed)) return;
    this.pendingSmartTranscript = this.pendingSmartTranscript
      ? `${this.pendingSmartTranscript} ${trimmed}`
      : trimmed;
  }

  /** Drop only the snapshot that was dispatched; keep speech that arrived during the request. */
  private consumePendingSmartTranscript(sent: string): void {
    const sentTrimmed = sent.trim();
    if (!sentTrimmed) return;
    const pending = this.pendingSmartTranscript.trim();
    if (pending === sentTrimmed) {
      this.pendingSmartTranscript = '';
      return;
    }
    if (pending.startsWith(sentTrimmed)) {
      this.pendingSmartTranscript = pending.slice(sentTrimmed.length).trim();
    }
  }

  private liveSmartNoteHtml(doc: CopilotDocument | null): string | null {
    const docKey = this.getDocumentKey(doc);
    if (!docKey) return null;

    const selectedKey = this.getDocumentKey(this.selectedDocument);
    if (this.editor && selectedKey === docKey) {
      return this.editor.getText().trim() ? this.editor.getHTML() : '';
    }

    if (
      this.pendingUpdate
      && `${this.pendingUpdate.templateId}_${this.pendingUpdate.templateType}` === docKey
    ) {
      return this.pendingUpdate.html;
    }

    return null;
  }

  private getSmartCurrentNote(doc: CopilotDocument | null): string {
    const html = this.liveSmartNoteHtml(doc) ?? this.smartCurrentNoteHtml;
    if (html != null) {
      return serializeNoteToMarkdown(html);
    }
    if (!doc?.sections) return '';
    const hasText = Object.values(doc.sections).some(section => !!section.text?.trim());
    return hasText ? serializeNoteToMarkdown(this.buildContentFromDoc(doc)) : '';
  }

  private surfaceSmartDictationFailure(): void {
    this.snackBar.open(
      this.translate.instant('Something went wrong.'),
      this.translate.instant('Close'),
      { duration: 4000 },
    );
  }

  private startSmartDictationOnPause(): void {
    this.smartAbortController?.abort();
    const controller = new AbortController();
    this.smartAbortController = controller;
    const generation = ++this.smartGenerationId;
    this.smartProcessingTask = this.runSmartDictationOnPause(controller.signal, generation).finally(() => {
      if (this.smartGenerationId === generation) {
        this.smartProcessingTask = null;
      }
    });
  }

  private async awaitSmartProcessingForDocument(doc: CopilotDocument | null): Promise<void> {
    if (!doc) return;
    const key = this.getDocumentKey(doc);
    while (this.smartProcessingTask && this.smartProcessingDocumentKey === key) {
      await this.smartProcessingTask;
    }
  }

  private isDocumentSmartProcessing(templateId: number, templateType: string): boolean {
    return this.isSmartProcessing
      && this.smartProcessingDocumentKey === `${templateId}_${templateType}`;
  }

  private clearSmartHighlight(): void {
    if (this.smartHighlightTimer !== null) {
      clearTimeout(this.smartHighlightTimer);
      this.smartHighlightTimer = null;
    }
    if (this.editor) {
      clearAiChangeDecoration(this.editor);
    }
  }

  private applySmartDictationHighlight(ranges: AiChangeDecorationRange[]): void {
    if (!this.editor || ranges.length === 0) return;
    this.clearSmartHighlight();
    setAiChangeDecorations(this.editor, ranges);
    this.ngZone.runOutsideAngular(() => {
      this.smartHighlightTimer = setTimeout(() => {
        this.clearSmartHighlight();
      }, 2500);
    });
  }

  private nextSmartPauseSeq(key: string): number {
    const next = (this.smartPauseSeqByKey.get(key) ?? 0) + 1;
    this.smartPauseSeqByKey.set(key, next);
    return next;
  }

  private getSmartNoteRevision(key: string): number {
    return this.smartNoteRevisionByKey.get(key) ?? 0;
  }

  private bumpSmartNoteRevision(key: string): number {
    const next = this.getSmartNoteRevision(key) + 1;
    this.smartNoteRevisionByKey.set(key, next);
    return next;
  }

  private isCurrentSmartGeneration(generation: number, signal: AbortSignal): boolean {
    return generation === this.smartGenerationId && !signal.aborted;
  }

  private async runSmartDictationOnPause(signal: AbortSignal, generation: number): Promise<void> {
    const awaitingTrailingFinal = this.isTranscriptionStreaming || this.waitingForFinalTranscript;
    if (!this.pendingSmartTranscript.trim() && !awaitingTrailingFinal) {
      return;
    }

    this.isSmartProcessing = true;
    this.updateEditorDisabledState();
    this.cdr.detectChanges();

    try {
      const targetKey = this.activeRecordingDocumentKey ?? this.getDocumentKey(this.selectedDocument);
      const targetDoc = this.findDocumentByKey(targetKey);
      if (!targetKey || !this.dictation || !targetDoc || targetDoc.approved) return;

      const oldText = (this.editor && this.getDocumentKey(this.selectedDocument) === targetKey)
        ? extractDocText(this.editor.state.doc)
        : '';

      this.smartProcessingDocumentKey = targetKey;
      this.smartCurrentNoteHtml = this.liveSmartNoteHtml(targetDoc);
      this.updateEditorDisabledState();
      this.cdr.detectChanges();

      await this.waitForFinalUtterance(2000, signal);
      if (!this.isCurrentSmartGeneration(generation, signal)) return;

      const doc = this.findDocumentByKey(targetKey);
      if (!this.dictation || !doc || doc.approved) return;

      const content = this.pendingSmartTranscript.trim();
      // Pause with no new speech is a no-op: do not POST and do not toast.
      if (!content) {
        return;
      }

      const pauseSeq = this.nextSmartPauseSeq(targetKey);
      const noteRevisionAtDispatch = this.getSmartNoteRevision(targetKey);
      await this.processing.processSmartSync(this.dictation, {
        templateId: doc.templateId,
        templateType: doc.templateType,
        templateTitle: doc.templateTitle,
        content,
        currentNote: this.getSmartCurrentNote(doc),
        previous: doc,
        pauseSeq,
        noteRevision: noteRevisionAtDispatch,
        signal,
        isStale: () =>
          !this.isCurrentSmartGeneration(generation, signal)
          || this.smartPauseSeqByKey.get(targetKey) !== pauseSeq
          || this.getSmartNoteRevision(targetKey) !== noteRevisionAtDispatch,
      });
      if (!this.isCurrentSmartGeneration(generation, signal)) return;
      if (this.smartPauseSeqByKey.get(targetKey) !== pauseSeq) return;
      if (this.getSmartNoteRevision(targetKey) !== noteRevisionAtDispatch) return;

      this.consumePendingSmartTranscript(content);
      this.discardPendingUpdateForDocument(targetKey);
      this.reloadProcessedDocuments();
      this.bumpSmartNoteRevision(targetKey);
      if (this.editor && this.hasDocuments && this.getDocumentKey(this.selectedDocument) === targetKey) {
        this.setEditorContentFromIndex(this.selectedTemplateIndex);
        const ranges = computeDiffHighlightRanges(oldText, this.editor.state.doc);
        if (ranges.length > 0) {
          this.applySmartDictationHighlight(ranges);
        }
      }
    } catch (error) {
      if (
        !this.isCurrentSmartGeneration(generation, signal)
        || error instanceof SmartDictationStaleError
        || (error instanceof Error && error.name === 'AbortError')
      ) {
        return;
      }
      console.error('Smart dictation generation failed:', error);
      this.surfaceSmartDictationFailure();
    } finally {
      if (generation === this.smartGenerationId) {
        this.isSmartProcessing = false;
        this.smartProcessingDocumentKey = null;
        this.smartCurrentNoteHtml = null;
        this.updateEditorDisabledState();
        this.cdr.detectChanges();
      }
    }
  }

  private async insertTranscriptText(text: string): Promise<void> {
    if (!this.editor) return;
    const content = this.textService.isAllCapsMode ? text.toUpperCase() : text;
    this.editor.commands.insertContent(this.checkForCommand(content) + ' ');
    if (this.isVoiceSelectionModeActive) {
      await this.exitVoiceSelectionMode();
    }
  }

  availableCommands: { label: string; name: string }[] = [];
  filteredCommands: { label: string; name: string }[] = [];
  commandFilter: FormControl = new FormControl('');

  onSelectCommand(): void {
    if (!this.selectedCommand) return;
    const name = this.selectedCommand;
    const cmd: FrontendPhrase = { name, parameters: [] };
    this.applyCommand(cmd);
  }

  // helper methods moved to TextService

  async ngOnInit(): Promise<void> {
    // Get dictation ID from route parameter
    const dictationId = this.route.snapshot.paramMap.get('_id');
    if (dictationId) {
      this.loadingDictation = true;
    }
    this.showTestCommands = this.flags.isEnabled('showTestCommands');
    this.showToolsPanel = this.flags.isEnabled('contentPanel');
    this.rightPaneOpen = this.showToolsPanel;

    // Build commands list for testing select (unique by alias)
    this.availableCommands = [];
    const seenAliases = new Set<string>();
    for (const p of (PHRASES || [])) {
      const alias = p.alias || p.name;
      if (seenAliases.has(alias)) continue;
      seenAliases.add(alias);
      this.availableCommands.push({ label: p.name, name: alias });
    }
    this.filteredCommands = [...this.availableCommands];
    this.commandFilter.valueChanges
      .pipe(debounceTime(150), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((q: string | null) => {
        const query = (q || '').trim().toLowerCase();
        if (!query) {
          this.filteredCommands = [...this.availableCommands];
          return;
        }
        this.filteredCommands = this.availableCommands.filter(c =>
          c.label.toLowerCase().includes(query) || c.name.toLowerCase().includes(query)
        );
      });

    // Prompt for microphone permission on page load
    await this.recorderService.loadMicrophones();
    this.recorderService.availableMicrophones$
      .pipe(takeUntil(this.destroy$))
      .subscribe(mics => {
        this.hasMicrophoneAccess = (mics || []).length > 0;
      });

    // Check if navigation came from dictation flow
    const navigationState = this.router.getCurrentNavigation()?.extras?.state || (history.state as any);
    const isDictationMode = navigationState?.mode === 'dictation';
    this.isDictationMode = isDictationMode;

    // Check if navigation came from encounters page
    if (navigationState?.fromEncounters) {
      this.fromEncounters = true;
      this.encountersPage = navigationState?.encountersPage ?? null;
    }

    // Fetch dictation data from API
    if (dictationId) {
      try {
        // Try to get from API first
        try {
          this.dictation = await this.apiService.getDictation(dictationId);
        } catch (apiError) {
          // If API fails (offline), try to get from processing service's local storage
          console.log('API failed to load dictation, checking local storage...', apiError);
          const localDictation = this.processing.getDictation(dictationId);
          if (localDictation) {
            console.log('Found dictation in local storage');
            this.dictation = localDictation;
          } else {
            throw apiError; // Re-throw if we can't find it locally either
          }
        }

        if (this.dictation?.type === 'backend' && (this.dictation?.status === 'processing' || this.dictation?.status === 'pending')) {
          await this.router.navigate(['/upload', dictationId]);
          return;
        }


        try {
          const rawUtterances = await this.apiService.getUtterances(dictationId);
          this.transcriptUtterances = mapUtterancesToTranscript(rawUtterances);
        } catch (error) {
          console.error('Error fetching utterances:', error);
        }

        if (this.dictation?.copilot?.type === SessionType.Dictation) {
          this.isDictationMode = true;
        }

        // Open template selector if coming from dictation flow
        if (isDictationMode && this.dictation) {
          this.addNewTemplate();
          // Clear the mode flag from navigation state
          window.history.replaceState({ ...history.state, mode: null }, '');
        }

        this.processing.documentsUpdates$
          .pipe(takeUntil(this.destroy$))
          .subscribe((evt: { dictationId: string; copilotDocument: CopilotDocument } | null) => {
            if (!evt || !this.dictation) return;
            if (evt.dictationId !== this.dictation._id) return;
            const copilotDocument = evt.copilotDocument;

            // Skip updates for documents that were previously deleted
            // This prevents ReplaySubject from replaying old updates for deleted documents
            if (this.processing.isDocumentDeleted(evt.dictationId, copilotDocument.templateId, copilotDocument.templateType)) {
              return;
            }

            // Store the current document info before updating
            const currentDoc = this.selectedDocument;
            const currentDocKey = currentDoc
              ? `${currentDoc.templateId}_${currentDoc.templateType}`
              : null;
            const incomingDocKey = `${copilotDocument.templateId}_${copilotDocument.templateType}`;
            const isCurrentDocument = currentDocKey === incomingDocKey;

            // Check if there's an active job for the incoming document
            const hasActiveJob = this.isJobActive(
              evt.dictationId,
              copilotDocument.templateId,
              copilotDocument.templateType
            );

            // If the incoming update is for the currently selected document and the user has local edits,
            // avoid overwriting. If the editor is still showing the last programmatic content (no local edits),
            // it's safe to update (e.g., when async generation completes).
            if (isCurrentDocument && !hasActiveJob && this.editor) {
              const docKey = `${copilotDocument.templateId}_${copilotDocument.templateType}`;
              const lastSaved = this.lastSavedContent.get(docKey);
              const currentHtml = (this.editor.getHTML());
              const hasLocalEdits = !!lastSaved && currentHtml !== lastSaved;
              if (hasLocalEdits) {
                console.log('Ignoring WebSocket update for currently edited document to preserve user changes');
                this.updateEditorDisabledState();
                return;
              }
            }

            // Ensure containers exist so we don't drop simultaneous updates
            const docs = this.ensureCopilotDocuments();
            this.upsertDocument(docs.original, copilotDocument);
            this.upsertDocument(docs.processed, copilotDocument);

            // Re-sort after upserting to maintain consistent order
            docs.original = sortByTemplateTitle(docs.original);
            docs.processed = sortByTemplateTitle(docs.processed);

            // Reload the processedDocuments array from dictation
            this.reloadProcessedDocuments();
            if (this.isFreeTextRecipientDisabled) {
              void this.removeFreeTextRecipients();
            }

            // Keep the selected document stable even if sorting changes array indices.
            // This prevents tabs from "jumping" to a different document during generation.
            let selectedIndexToUse = this.selectedTemplateIndex;
            if (currentDocKey) {
              const stableIndex = this.processedDocuments.findIndex(
                d => `${d.templateId}_${d.templateType}` === currentDocKey
              );
              if (stableIndex >= 0 && stableIndex !== this.selectedTemplateIndex) {
                this.selectedTemplateIndex = stableIndex;
                selectedIndexToUse = stableIndex;
              }
            }

            // Update the editor when either:
            // - the incoming update is for the currently selected document, or
            // - the selected index was re-mapped to keep the same document selected.
            const nowSelectedDoc = this.processedDocuments[selectedIndexToUse];
            const nowSelectedKey = nowSelectedDoc
              ? `${nowSelectedDoc.templateId}_${nowSelectedDoc.templateType}`
              : null;
            const shouldRefreshEditor = this.editor && this.hasDocuments && (incomingDocKey === nowSelectedKey);
            if (shouldRefreshEditor) {
              this.setEditorContentFromIndex(selectedIndexToUse);
            }

            // Re-evaluate regenerating state once data updates come in
            this.updateEditorDisabledState();
            this.trackRecipientFieldsDisabledViewed();
          });
        // Track processing jobs to toggle editor editable state
        this.processing.processing$
          .pipe(takeUntil(this.destroy$))
          .subscribe((jobs: any[]) => {
            this.currentJobs = jobs || [];
            this.updateEditorDisabledState();
            this.notifyWhenUpdateJobsComplete(jobs || []);
          });
        // Subscribe to any regenerate/processing failure and show inline error banners.
        merge(this.processing.templateNotFoundError$, this.processing.processingError$$)
          .pipe(takeUntil(this.destroy$))
          .subscribe(({ dictationId, templateId, templateType }) => {
            if (dictationId !== this.dictation?._id) return;
            this.regenerate.flagError(templateId, templateType);
            this.cdr.detectChanges();
          });
        // Load processed documents from dictation
        this.loadProcessedDocuments();
        if (this.isFreeTextRecipientDisabled) {
          void this.removeFreeTextRecipients();
        }

        // Fetch the latest Hoffweb workflow trigger status per template so the
        // workflow UI reflects the current toggle state rather than the value
        // captured at document creation time (TDP-2399). Runs non-blocking so the
        // editor renders immediately; workflow UI updates reactively when resolved.
        void this.getTemplates().then(() => this.refreshWorkflowTriggers());

        // Recover any orphaned async jobs from prior sessions
        this.processing.recoverOrphanedJobs();
      } catch (error) {
        console.error('Error fetching dictation:', error);
      } finally {
        this.loadingDictation = false;
      }
    }

    // Subscribe to recording state to track connectivity.
    // After Socket.IO auto-reconnects, state changes may arrive outside Angular's
    // zone, so detectChanges is needed to re-evaluate isRecordButtonDisabled.
    this.recorderService.recordingState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.recordingState = state;
        if (state === RecordingState.Recording) {
          this.clearSmartHighlight();
          // Always set the document key when recording starts
          // This ensures transcriptions go to the correct document even if the user
          // previously paused on a different document and then started recording on a new one
          this.activeRecordingDocumentKey = this.getDocumentKey(this.selectedDocument);
          this.smartPauseArmed = true;
        } else {
          if (state === RecordingState.Disconnected) {
            // Only clear when fully disconnected - not when paused
            // This ensures pending transcriptions are still applied to the correct document
            this.activeRecordingDocumentKey = null;
            this.isTranscriptionStreaming = false;
            this.waitingForFinalTranscript = false;
            this.activeTranscriptUtteranceIndex = null;
            this.smartPauseArmed = false;
          } else if (state === RecordingState.Initiated) {
            this.smartPauseArmed = false;
          }
          this.textService.disableAllCaps();
        }
        if (state !== RecordingState.Recording && this.isVoiceSelectionModeActive) {
          this.exitVoiceSelectionMode().catch(error => console.error('Failed to exit voice selection mode', error));
        }
        // RecorderService.pause() emits Loading between Recording and Paused, so a
        // direct Recording→Paused check never fires in the real pause path.
        if (state === RecordingState.Paused && this.smartPauseArmed && this.isSmartDictationActive) {
          this.smartPauseArmed = false;
          this.startSmartDictationOnPause();
        }
        this.cdr.detectChanges();
      });

    // Drive wave indicator directly from the WebSocket stream.
    // After Socket.IO auto-reconnects, events through the RecordButtonComponent
    // @Output() chain may arrive outside Angular's zone, skipping change detection.
    this.recorderService.frontendResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (!result || result.type !== 'result') return;
        // Ignore results when this tab isn't actively recording — another tab
        // may own the dictation and the server fans results to all sockets.
        // Exception: keep updating through the pause Loading gap and while we
        // await the trailing final, so waitForFinalUtterance actually waits.
        if (
          this.recordingState !== RecordingState.Recording
          && !this.isTranscriptionStreaming
          && !this.smartPauseArmed
          && !this.isSmartProcessing
        ) {
          this.cdr.detectChanges();
          return;
        }
        const selectedKey = this.selectedDocument ? this.getDocumentKey(this.selectedDocument) : null;
        if (!this.activeRecordingDocumentKey || selectedKey !== this.activeRecordingDocumentKey) {
          this.isTranscriptionStreaming = false;
          this.cdr.detectChanges();
          return;
        }
        this.isTranscriptionStreaming = !result.final;
        this.cdr.detectChanges();
      });
  }

  onCommandsOpened(opened: boolean): void {
    if (!opened) return;
    // Focus search input after panel opens
    setTimeout(() => {
      const el = document.getElementById('commandSearchInput') as HTMLInputElement | null;
      el?.focus();
    }, 0);
  }

  ngAfterViewInit() {
    // Initialise editor after view is ready
    setTimeout(() => {
      this.initializeEditor();
    }, 100);
  }

  async ngOnDestroy() {
    this.smartAbortController?.abort();
    this.clearSmartHighlight();
    // Flush any pending save before destroying
    await this.flushPendingUpdate();
    this.recorderService.pause();
    this.recorderService.disconnectAndCleanup();
    if (this.editor) {
      this.editor.destroy();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeEditor() {
    if (this.editor) {
      return;
    }
    if (this.editorContainer?.nativeElement) {
      this.editor = new Editor({
        element: this.editorContainer.nativeElement,
        extensions: createEditorExtensions(),
        content: '',
        editable: !this.isRegenerating,
        coreExtensionOptions: {
          clipboardTextSerializer: { blockSeparator: CLIPBOARD_BLOCK_SEPARATOR },
        },
        editorProps: {
          attributes: {
            class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-[300px] px-4 pt-0 pb-4'
          },
          handleDOMEvents: {
            mousedown: (_view, event) => {
              if (!this.isManualEditingLockedForVoiceSelection()) return false;
              event.preventDefault();
              return true;
            },
            keydown: (_view, event) => {
              if (!this.isManualEditingLockedForVoiceSelection()) return false;
              event.preventDefault();
              return true;
            }
          }
        }
      });
      this.editor.on('update', ({ editor }) => {
        let html = editor.getHTML();
        if (this.programmaticHtmlToIgnore !== null && html === this.programmaticHtmlToIgnore) {
          this.programmaticHtmlToIgnore = null;
          return;
        }
        if (this.isVoiceSelectionModeActive) {
          return;
        }
        const currentDoc = this.selectedDocument;
        if (currentDoc) {
          const update = {
            html,
            templateId: currentDoc.templateId,
            templateType: currentDoc.templateType
          };
          this.pendingUpdate = update;
          this.bumpSmartNoteRevision(`${currentDoc.templateId}_${currentDoc.templateType}`);
          this.editorUpdate$.next(update);
        }
      });
      if (!this.editorAutosaveBound) {
        this.editorAutosaveBound = true;
        this.editorUpdate$.pipe(
          debounceTime(5000),
          distinctUntilChanged((prev, curr) =>
            prev.html === curr.html &&
            prev.templateId === curr.templateId &&
            prev.templateType === curr.templateType
          ),
          takeUntil(this.destroy$))
          .subscribe(update => this.applyDebouncedEditorUpdate(update));
      }
      this.setEditorContentFromIndex(this.selectedTemplateIndex);
      this.updateEditorDisabledState();
    }
  }

  private buildContentFromDoc(doc: CopilotDocument | undefined): string {
    let content = '';
    if (doc && doc.sections) {
      Object.entries(doc.sections).forEach(([_, section]) => {
        if (section.title) {
          content += `<h3>${section.title}</h3>`;
        }
        if (section.text) {
          content += parseMarkdownToHtml(section.text);
        }
      });
    }
    return content || '<p></p>';
  }

  private reloadProcessedDocuments(): void {
    if (this.dictation?.copilot?.documents?.processed) {
      this.processedDocuments = sortByTemplateTitle(
        this.dictation.copilot.documents.processed.filter(doc => !doc._delete)
      );
      this.hasDocuments = this.processedDocuments.length > 0;
    }
  }

  private loadProcessedDocuments() {
    // Validate and reload processed documents
    this.reloadProcessedDocuments();

    if (this.hasDocuments) {
      // Update editor content if editor is already initialised
      if (this.editor) {
        this.setEditorContentFromIndex(this.selectedTemplateIndex);
        this.updateEditorDisabledState();
      }
    } else {
      this.processedDocuments = [];
      this.hasDocuments = false;
      console.log('No processed documents found');
    }

    // Clear waiting state when no documents are present
    if (!this.hasDocuments) {

      // Clear waiting state when no documents are present
      this.isRegenerating = false;
      this.isTranscriptionStreaming = false;
      this.pendingUpdate = null;
      this.updateEditorDisabledState();
    }
    this.trackRecipientFieldsDisabledViewed();
  }

  /**
   * Fetches the latest Hoffweb templates and stores them on `templates`. Skips when
   * offline (dictation may have been loaded from local cache).
   */
  private async getTemplates(): Promise<void> {
    if (!navigator.onLine) return;

    try {
      this.templates = await this.apiService.getCopilotTemplates();
    } catch (err) {
      console.error('Failed to fetch templates for workflow_trigger refresh', err);
      this.templates = null;
    }
  }

  /**
   * Returns whether workflow trigger is enabled for a template. Reads from the latest
   * Hoffweb templates when available; falls back to the document snapshot otherwise
   * (e.g. while templates are loading or when offline).
   */
  private isWorkflowTriggerEnabled(
    templateId: number,
    templateType: CopilotDocument['templateType']
  ): boolean {
    if (this.templates) {
      const template = this.templates.find(t => t.id === templateId && t.type === templateType);
      return template ? !!template.workflow_trigger : false;
    }

    const doc = this.processedDocuments.find(
      d => d.templateId === templateId && d.templateType === templateType
    );
    return !!doc?.workflow_trigger;
  }

  /**
   * Refreshes each document's workflow_trigger from `templates` so the workflow UI
   * reflects the current toggle state rather than the value snapshotted at document
   * creation time (TDP-2399). A template missing from the list is treated as disabled.
   * The refreshed value is not persisted back to the document.
   */
  private refreshWorkflowTriggers(): void {
    const documents = this.dictation?.copilot?.documents;
    if (!documents?.processed?.length || !this.templates) return;

    let changed = false;
    for (const doc of [...documents.processed, ...(documents.original || [])]) {
      const latest = this.isWorkflowTriggerEnabled(doc.templateId, doc.templateType);
      if (doc.workflow_trigger !== latest) {
        doc.workflow_trigger = latest;
        changed = true;
      }
    }
    if (changed) {
      // Re-evaluate workflow-dependent UI (priority selector, approve flow).
      this.reloadProcessedDocuments();
      this.updateEditorDisabledState();
      this.cdr.markForCheck();
    }
    this.trackRecipientFieldsDisabledViewed();
  }

  private async reloadDictation(): Promise<void> {
    const dictationId = this.dictation?._id;
    if (!dictationId) return;

    this.dictation = await this.apiService.getDictation(dictationId);
    this.loadProcessedDocuments();
  }

  private getActiveSectionKey(): string {
    const doc = this.selectedDocument;
    if (!doc) return 'BODY';
    const keys = Object.keys(doc.sections || {});
    if (keys.length === 1) return keys[0];
    return 'BODY';
  }

  private getDocumentKey(doc: CopilotDocument | null | undefined): string | null {
    if (!doc) return null;
    return `${doc.templateId}_${doc.templateType}`;
  }

  /** Stable identity for document tabs/options so Material does not recreate them on each persist. */
  trackByDocumentKey(_index: number, doc: CopilotDocument): string {
    return `${doc.templateId}_${doc.templateType}`;
  }

  private findDocumentByKey(key: string | null): CopilotDocument | null {
    if (!key) return null;
    return this.processedDocuments.find(doc => this.getDocumentKey(doc) === key) ?? null;
  }

  private matchesPendingUpdate(update: { html: string; templateId: number; templateType: string }): boolean {
    return !!this.pendingUpdate
      && this.pendingUpdate.html === update.html
      && this.pendingUpdate.templateId === update.templateId
      && this.pendingUpdate.templateType === update.templateType;
  }

  private discardPendingUpdateForDocument(docKey: string | null): void {
    if (!docKey || !this.pendingUpdate) return;
    const pendingKey = `${this.pendingUpdate.templateId}_${this.pendingUpdate.templateType}`;
    if (pendingKey === docKey) {
      this.pendingUpdate = null;
    }
  }

  private applyDebouncedEditorUpdate(update: { html: string; templateId: number; templateType: string }): void {
    if (this.isVoiceSelectionModeActive) {
      return;
    }
    // Ignore emissions whose pending save was discarded (flush, tab switch, smart persist).
    if (!this.matchesPendingUpdate(update)) {
      return;
    }
    const docKey = `${update.templateId}_${update.templateType}`;
    const lastSaved = this.lastSavedContent.get(docKey);

    // Skip if this content was already saved (e.g., by flushPendingUpdate)
    if (lastSaved === update.html) {
      this.pendingUpdate = null;
      return;
    }


    this.saveSectionHtml(update.html, update.templateId, update.templateType);

    if (this.pendingUpdate?.templateId === update.templateId &&
      this.pendingUpdate?.templateType === update.templateType &&
      this.pendingUpdate?.html === update.html) {
      this.pendingUpdate = null;
    }
  }

  private async saveSectionHtml(html: string, templateId: number, templateType: string): Promise<void> {
    if (!this.dictation) return;
    if (this.isDocumentSmartProcessing(templateId, templateType)) return;

    // Find the document that was being edited using the captured template info
    const docToSave = this.processedDocuments.find(
      doc => doc.templateId === templateId && doc.templateType === templateType
    );
    if (!docToSave) return;
    if (docToSave.approved) return;

    const sectionKey = 'BODY'; // Use the same logic as getActiveSectionKey but for the specific doc
    const keys = Object.keys(docToSave.sections || {});
    const actualSectionKey = keys.length === 1 ? keys[0] : 'BODY';
    const currentTitle = docToSave.sections?.[actualSectionKey]?.title ?? '';

    try {
      this.beginSaving();
      await this.apiService.updateDictationCopilot(this.dictation._id, {
        copilot: {
          documents: {
            processed: [
              {
                ...this.processedDocumentPatch(docToSave),
                sections: {
                  [actualSectionKey]: {
                    title: currentTitle,
                    text: fillEmptyParagraphsWithInvisibleCharacter(html)
                  }
                }
              }
            ]
          }
        }
      });

      this.analytics.track('document_manually_edited', {
        encounter_id: this.dictation._id,
        blueprint_id: String(templateId),
      });

      // Track that this content was saved
      const docKey = `${templateId}_${templateType}`;
      this.lastSavedContent.set(docKey, html);


      // Update local state for the document that was edited
      const updatedSections = { ...(docToSave.sections || {}) };
      updatedSections[actualSectionKey] = { title: currentTitle, text: html };
      docToSave.sections = updatedSections;

      // Keep dictation cache in sync if present
      const dictationDocIndex = this.dictation?.copilot?.documents?.processed?.findIndex(
        d => d.templateId === templateId && d.templateType === templateType
      );
      if (dictationDocIndex !== undefined && dictationDocIndex >= 0 && this.dictation?.copilot?.documents?.processed) {
        const dictationDoc = this.dictation.copilot.documents.processed[dictationDocIndex];
        dictationDoc.sections = {
          ...(dictationDoc.sections || {}),
          [actualSectionKey]: { title: currentTitle, text: html }
        };
      }
    } catch (e) {
      console.error('Failed to update section content', e);
    } finally {
      this.endSaving();
    }
  }

  private beginSaving(): void {
    this.savingCount++;
    this.isSaving = this.savingCount > 0;
  }

  private endSaving(): void {
    this.savingCount = Math.max(0, this.savingCount - 1);
    this.isSaving = this.savingCount > 0;
  }

  private async flushPendingUpdate(): Promise<void> {
    if (!this.pendingUpdate) {
      return;
    }

    const update = this.pendingUpdate;
    if (this.isDocumentSmartProcessing(update.templateId, update.templateType)) {
      return;
    }

    const docKey = `${update.templateId}_${update.templateType}`;
    const lastSaved = this.lastSavedContent.get(docKey);

    // Skip if this content was already saved
    if (lastSaved === update.html) {
      this.pendingUpdate = null;
      return;
    }

    this.pendingUpdate = null;

    await this.saveSectionHtml(update.html, update.templateId, update.templateType);
  }

  // removed content builder; content is set via setContent at usage sites

  async onTemplateChange(index: number) {
    this.clearSmartHighlight();
    await this.exitVoiceSelectionMode();
    const newDoc = this.processedDocuments[index];
    const newDocKey = this.getDocumentKey(newDoc);
    const isDocumentChanging = this.activeRecordingDocumentKey && newDocKey !== this.activeRecordingDocumentKey;

    if (this.recorderService.isRecording()) {
      await this.stopRecordingIfActive();
      if (isDocumentChanging) {
        await this.waitForFinalUtterance();
      }
    }

    await this.flushPendingUpdate();

    if (isDocumentChanging) {
      this.activeRecordingDocumentKey = null;
    }
    this.isTranscriptionStreaming = false;

    this.selectedTemplateIndex = index;
    if (this.editor) {
      this.setEditorContentFromIndex(this.selectedTemplateIndex);
    }
    this.updateEditorDisabledState();
    this.trackRecipientFieldsDisabledViewed();

    if (newDoc) {
    }
  }

  addNewTemplate() {
    if (!this.retention.canGenerate) return;
    const singleSelection = this.isSmartDictationActive && !this.hasDocuments;
    const dialogRef = this.dialog.open(SelectTemplateComponent, {
      data: {
        dictation: this.dictation,
        singleSelection,
      },
      size: 'mdPlus',
    });
    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (result: any) => {
        if (result?.templates) {
          const selectedTemplates = result.templates;
          for (const template of selectedTemplates) {
            this.analytics.track('document_created', {
              encounter_id: this.dictation?._id,
              blueprint_id: String(template.id),
              surface: 'document',
              trigger: 'manual',
            });
          }
        }
        if (result?.defaultDocumentsCreated) {
          await this.reloadDictation();
        }
        this.onTemplateChange(this.selectedTemplateIndex);
      });
  }

  async copyContent() {
    if (this.editor) {
      const htmlContent = this.editor.getHTML();
      const textContent = this.editor.getText({ blockSeparator: CLIPBOARD_BLOCK_SEPARATOR });

      try {
        // Create clipboard item with both HTML and plain text formats
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
          'text/plain': new Blob([textContent], { type: 'text/plain' })
        });

        await navigator.clipboard.write([clipboardItem]);

        this.snackBar.open(
          this.translate.instant('Content copied to clipboard'),
          this.translate.instant('Close'),
          { duration: 3000, horizontalPosition: 'right', verticalPosition: 'bottom' }
        );
      } catch (error) {
        // Fallback to plain text if clipboard API fails
        try {
          await navigator.clipboard.writeText(textContent);
          this.snackBar.open(
            this.translate.instant('Content copied to clipboard'),
            this.translate.instant('Close'),
            { duration: 3000, horizontalPosition: 'right', verticalPosition: 'bottom' }
          );
        } catch {
          this.snackBar.open(
            this.translate.instant('Failed to copy content'),
            this.translate.instant('Close'),
            { duration: 3000, horizontalPosition: 'right', verticalPosition: 'bottom' }
          );
        }
      }
    }
  }

  async approveDocument() {
    const selectedDoc = this.selectedDocument;
    if (!selectedDoc) return;

    // Stop recording if currently active
    await this.stopRecordingIfActive();
    await this.awaitSmartProcessingForDocument(selectedDoc);

    // Ensure the latest edit is persisted before approving (avoids fast approve-before-save race)
    await this.flushPendingUpdate();

    const workflowTriggerEnabled = this.isWorkflowTriggerEnabled(selectedDoc.templateId, selectedDoc.templateType);

    // If workflow trigger is enabled for this document, show the new workflow approval dialog and stop here
    if (workflowTriggerEnabled) {
      const formattedName = formatDictationSubjectName(this.dictation?.copilot);
      const ref = this.dialog.open(ApproveWorkflowDialogComponent, {
        size: 'md',
        data: {
          title: this.translate.instant('Approve document'),
          description: this.translate.instant('Approving this document will save it as final (no edits) and trigger any associated task in T‑Pro EHR'),
          dictationId: this.dictation?._id,
          dictationDuration: this.dictation?.duration ?? 0,
          externalTemplateId: selectedDoc.templateId,
          externalTemplateType: selectedDoc.templateType as any,
          priority: (selectedDoc.priority as 1 | 2 | 3) ?? 1,
          subjectName: formattedName ?? '',
          subjectFirstName: this.dictation?.copilot?.subject_first_name?.trim() ?? '',
          subjectMiddleName: this.dictation?.copilot?.subject_middle_name?.trim() ?? '',
          subjectLastName: this.dictation?.copilot?.subject_last_name?.trim() ?? '',
          subjectAlternativeId: this.dictation?.copilot?.subject_alternative_id ?? '',
          subjectId: this.dictation?.copilot?.subject_id ?? '',
          subjectDob: this.dictation?.copilot?.subject_dob ?? '',
          visitId: this.dictation?.copilot?.subject_visit_id?.trim() ?? '',
          recipients: selectedDoc.recipients,
          recipientManagementEnabled: this.canManageRecipients(selectedDoc),
        }
      });
      ref.afterClosed().subscribe(async (taskId: number | null) => {
        if (taskId) {
          const selected = this.selectedDocument;
          if (!this.dictation || !selected) return;
          try {
            await this.removeFreeTextRecipientsBeforeApprove(selected);
            const isScribeOnlyApproval = taskId === -1;
            const processedDoc: any = {
              ...this.processedDocumentPatch(selected),
              sections: {},
              approved: true
            };
            if (!isScribeOnlyApproval) {
              processedDoc.taskId = taskId;
            }
            await this.apiService.updateDictationCopilot(this.dictation._id, {
              copilot: {
                documents: {
                  processed: [processedDoc]
                }
              }
            });
            this.processedDocuments[this.selectedTemplateIndex].approved = true;
            this.updateEditorDisabledState();
            this.analytics.track('document_approved', {
              encounter_id: this.dictation._id,
              blueprint_id: String(selected.templateId),
              surface: 'web_document',
              recipients_enabled: this.canManageRecipients(selected),
              has_primary: hasPrimaryRecipient(selected.recipients),
              cc_count: ccRecipients(selected.recipients).length,
              free_text_count: ccRecipients(selected.recipients).filter((recipient) => isManualRecipient(recipient)).length
                + (isManualRecipient(selected.recipients?.primaryRecipient) ? 1 : 0),
            });
            this.snackBar.open(this.translate.instant('Document approved'), this.translate.instant('Close'), {
              duration: 2000,
              horizontalPosition: 'right',
              verticalPosition: 'bottom'
            });
          } catch (e) {
            this.snackBar.open(this.translate.instant('Failed to approve document'), this.translate.instant('Close'), {
              duration: 3000,
              horizontalPosition: 'right',
              verticalPosition: 'bottom'
            });
          }
        }
      });
      return;
    }

    const ref = this.dialog.open<ConfirmComponent, ConfirmDialogData, boolean>(ConfirmComponent, {
      data: {
        icon: 'check_circle',
        title: this.translate.instant('Release document'),
        description: this.translate.instant('By approving this document:\n\n1. You confirm that all information is accurate and complete to the best of your knowledge\n\n2. You acknowledge that approval will trigger any associated downstream workflows\n\nAre you sure you want to proceed?'),
        cancelText: this.translate.instant('Cancel'),
        confirmText: this.translate.instant('Confirm'),
        compactActions: true
      },
      presentation: 'dialog',
    });
    const confirmed = await firstValueFrom(ref.afterClosed());
    if (!confirmed || !this.dictation) return;

    try {
      await this.removeFreeTextRecipientsBeforeApprove(selectedDoc);
      await this.apiService.updateDictationCopilot(this.dictation._id, {
        copilot: {
          documents: {
            processed: [
              {
                ...this.processedDocumentPatch(selectedDoc),
                sections: {},
                approved: true
              } as any
            ]
          }
        }
      });
      // optimistic local update
      this.processedDocuments[this.selectedTemplateIndex].approved = true;
      this.updateEditorDisabledState();
      this.analytics.track('document_approved', {
        encounter_id: this.dictation._id,
        blueprint_id: String(selectedDoc.templateId),
        surface: 'web_document',
        recipients_enabled: this.canManageRecipients(selectedDoc),
        has_primary: hasPrimaryRecipient(selectedDoc.recipients),
        cc_count: ccRecipients(selectedDoc.recipients).length,
        free_text_count: ccRecipients(selectedDoc.recipients).filter((recipient) => isManualRecipient(recipient)).length
          + (isManualRecipient(selectedDoc.recipients?.primaryRecipient) ? 1 : 0),
      });
      this.snackBar.open(this.translate.instant('Document approved'), this.translate.instant('Close'), {
        duration: 2000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom'
      });
    } catch (e) {
      this.snackBar.open(this.translate.instant('Failed to approve document'), this.translate.instant('Close'), {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom'
      });
    }
  }

  async deleteNote() {
    if (this.selectedDocument?.approved) {
      return;
    }
    // Ask for confirmation first
    const documentTitle = this.selectedDocument?.templateTitle || 'Document';
    const ref = this.dialog.open<ConfirmComponent, ConfirmDialogData, boolean>(ConfirmComponent, {
      data: {
        icon: 'delete',
        title: `Delete ${documentTitle} Note?`,
        description: this.translate.instant('Are you sure you want to delete this document? This action cannot be undone.'),
        cancelText: this.translate.instant('Cancel'),
        confirmText: this.translate.instant('Yes, delete')
      },
      presentation: 'dialog',
    });
    const confirmed = await firstValueFrom(ref.afterClosed());
    if (!confirmed) {
      return;
    }

    if (!this.dictation) {
      return;
    }

    // Stop recording before deleting the document
    await this.stopRecordingIfActive();
    this.activeRecordingDocumentKey = null;
    this.isTranscriptionStreaming = false;

    const selectedDoc = this.processedDocuments[this.selectedTemplateIndex];
    if (!selectedDoc) {
      return;
    }
    selectedDoc._delete = true;

    // Mark document as deleted in processing service to prevent ReplaySubject from replaying old updates
    this.processing.markDocumentDeleted(this.dictation._id, selectedDoc.templateId, selectedDoc.templateType);

    const currentProcessed = this.dictation.copilot?.documents?.processed ?? [];
    const matchesSelected = (d: CopilotDocument) => d.templateId === selectedDoc.templateId && d.templateType === selectedDoc.templateType;

    // Locally, hide the deleted item from UI lists and sort to maintain consistent order
    const localProcessedAfterDelete = sortByTemplateTitle(
      currentProcessed.filter(d => !matchesSelected(d))
    );
    try {
      await this.apiService.updateDictationCopilot(this.dictation._id, {
        copilot: {
          documents: {
            original: [selectedDoc],
            processed: [selectedDoc]
          }
        }
      });
      this.dictation!.copilot!.documents.original = localProcessedAfterDelete;
      this.dictation!.copilot!.documents.processed = localProcessedAfterDelete;
      this.processedDocuments = localProcessedAfterDelete;
      this.hasDocuments = this.processedDocuments.length > 0;
      this.analytics.track('document_deleted', {
        blueprint_id: String(selectedDoc.templateId),
        encounter_id: this.dictation._id,
        was_approved: selectedDoc.approved === true,
      });

      // Adjust selection and update editor
      if (this.processedDocuments.length === 0) {
        this.selectedTemplateIndex = 0;
        // Clear any waiting state when no documents remain
        this.isRegenerating = false;
        this.isTranscriptionStreaming = false;
        this.pendingUpdate = null;
        this.updateEditorDisabledState();
      } else {
        this.selectedTemplateIndex = Math.min(this.selectedTemplateIndex, this.processedDocuments.length - 1);
      }
      this.onTemplateChange(this.selectedTemplateIndex);


      this.snackBar.open(this.translate.instant('Document deleted'), this.translate.instant('Close'), {
        duration: 2000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom'
      });
    } catch (error) {
      this.snackBar.open(this.translate.instant('Failed to delete blueprint'), this.translate.instant('Close'), {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom'
      });
    }
  }

  get canRegenerateDocument(): boolean {
    if (this.isSmartDictationActive)
      return false;
    return this.regenerate.canRegenerate(this.dictation, this.selectedDocument);
  }

  get showNoteGenerationSettings(): boolean {
    return this.flags.isEnabled('noteGenerationSettings');
  }

  get canRegenerateAllDocuments(): boolean {
    if (this.isSmartDictationActive)
      return false;
    return this.regenerate.canRegenerateAll(this.dictation, this.processedDocuments);
  }

  get disabledForSmartDictationTooltip(): string | null {
    return this.isSmartDictationActive ? 'Not available in smart dictation mode' : null;
  }

  get shouldPromptRegenerateAfterEdit(): boolean {
    return this.hasDocuments && this.regenerate.hasSourceMaterial(this.dictation);
  }

  get canEditPatient(): boolean {
    if (this.isSmartDictationActive)
      return false;
    return !this.hasApprovedDocuments() && this.retention.canEditPatient;
  }

  get displayTranscription(): string {
    return getDictationTranscription(this.dictation);
  }

  get notes() {
    return this.dictation?.copilot?.notes ?? [];
  }

  get showSyncAllBanner(): boolean {
    return this.retention.canGenerate && this.staleUnapprovedDocumentCount >= 1;
  }

  get isSyncAllDisabled(): boolean {
    return this.regenerate.isRegeneratingAll(this.dictation?._id) || this.isRegenerating || !this.canRegenerateAllDocuments;
  }

  get staleUnapprovedDocumentCount(): number {
    return this.processedDocuments.filter(
      (doc) => !doc.approved && this.freshness.isStale(this.dictation!, doc),
    ).length;
  }

  addNote(text: string): void {
    const trimmed = text.trim();
    if (!trimmed) {
      return;
    }

    const copilot = this.dictation!.copilot!;
    copilot.notes = [...(copilot.notes ?? []), { duration: 0, note: trimmed }];
    this.persistNotes('add_note');
  }

  deleteContextNote(index: number): void {
    const notes = this.dictation!.copilot!.notes ?? [];
    if (index < 0 || index >= notes.length) {
      return;
    }

    this.dictation!.copilot!.notes = notes.filter((_, i) => i !== index);
    this.persistNotes('delete_note');
  }

  private persistNotes(eventSource: 'add_note' | 'delete_note'): void {
    const dictation = this.dictation!;
    const copilot = dictation.copilot!;
    const notes = copilot.notes ?? [];
    const watermark = nextContentUpdatedAt(copilot.content_updated_at);
    copilot.notes = notes;
    copilot.content_updated_at = watermark;
    this.apiService.updateDictationCopilot(dictation._id, {
      copilot: { notes, content_updated_at: watermark },
    }).then(() => {
      if (eventSource === 'add_note') {
        this.analytics.track('context_note_added', {
          surface: 'document_screen',
          encounter_id: dictation._id,
        });
        return;
      }
      this.analytics.track('context_note_deleted', {
        surface: 'document_screen',
        encounter_id: dictation._id,
      });
    }).catch((err) => {
      console.error('Error updating notes:', err);
    });
  }

  async regenerateDocument(): Promise<void> {
    if (!this.canRegenerateDocument) return;

    const confirmed = await this.regenerate.confirmRegenerate();
    if (!confirmed || !this.dictation) return;

    const document = this.selectedDocument;
    this.analytics.track('regenerate_document_clicked', {
      encounter_id: this.dictation._id,
      ...(document?.templateId != null ? { blueprint_id: String(document.templateId) } : {}),
    });

    await this.reprocessSelectedDocument();
  }

  async openDetailsAndStyle(): Promise<void> {
    if (!this.canRegenerateDocument || !this.selectedDocument) return;

    const dialogRef = this.dialog.open<
      DetailsAndStyleComponent,
      DetailsAndStyleDialogData,
      DetailsAndStyleResult | undefined
    >(DetailsAndStyleComponent, {
      data: {
        userSettings: this.user?.settings?.noteGeneration,
        document: this.selectedDocument,
      },
      presentation: 'dialog',
      size: 'md',
    });
    const result = await firstValueFrom(dialogRef.afterClosed());
    if (!result?.applied || !this.selectedDocument) return;

    await this.reprocessSelectedDocument(
      hasNoteGenerationSettings(result.config) ? { config: result.config } : undefined,
    );
  }

  private async reprocessSelectedDocument(
    options?: { config?: NoteGenerationSettings },
  ): Promise<void> {
    if (!this.dictation) return;

    const selectedDoc = this.processedDocuments[this.selectedTemplateIndex];
    if (!selectedDoc) return;

    await this.stopRecordingIfActive();
    this.activeRecordingDocumentKey = null;
    this.isTranscriptionStreaming = false;

    const workflowTriggerEnabled = this.isWorkflowTriggerEnabled(selectedDoc.templateId, selectedDoc.templateType);
    const template: Template = {
      id: selectedDoc.templateId,
      type: selectedDoc.templateType,
      title: selectedDoc.templateTitle,
      workflow_trigger: workflowTriggerEnabled
    };

    if (options?.config) {
      this.processing.enqueue(this.dictation, [template], 'reprocess', { config: options.config });
    } else {
      this.processing.enqueue(this.dictation, [template], 'reprocess');
    }
    this.regenerate.clearError(selectedDoc, this.dictation._id);
    this.isRegenerating = true;
    this.updateEditorDisabledState();
  }

  async regenerateAllDocuments(options?: {
    skipConfirmation?: boolean;
    trigger?: 'regenerate_all' | 'sync';
  }): Promise<void> {
    if (this.regenerate.isRegeneratingAll(this.dictation?._id) || !this.canRegenerateAllDocuments) return;

    const confirmed = options?.skipConfirmation
      || await this.regenerate.confirmRegenerateAll(this.hasApprovedDocuments());
    if (!confirmed || !this.dictation) return;

    const trigger = options?.trigger ?? 'regenerate_all';
    const documentsToRegenerate = this.processedDocuments.filter(d =>
      !d.approved && !this.isDocumentGenerating(d));
    if (!documentsToRegenerate.length) return;

    if (trigger === 'sync') {
      this.analytics.track('sync_all_clicked', {
        encounter_id: this.dictation._id,
        document_count: documentsToRegenerate.length,
        was_suggested: this.staleUnapprovedDocumentCount >= 1,
      });
    } else if (options?.skipConfirmation) {
      this.analytics.track('regenerate_after_edit_clicked', {
        encounter_id: this.dictation._id,
        document_count: documentsToRegenerate.length,
      });
    } else {
      this.analytics.track('regenerate_all_clicked', {
        encounter_id: this.dictation._id,
        document_count: documentsToRegenerate.length,
      });
    }

    this.regenerate.watchRegenerateAll(this.dictation._id, documentsToRegenerate);

    await this.stopRecordingIfActive();
    this.activeRecordingDocumentKey = null;
    this.isTranscriptionStreaming = false;

    const templates: Template[] = documentsToRegenerate.map(doc => ({
      id: doc.templateId,
      type: doc.templateType,
      title: doc.templateTitle,
      workflow_trigger: this.isWorkflowTriggerEnabled(doc.templateId, doc.templateType)
    }));

    this.regenerate.clearErrors(documentsToRegenerate, this.dictation._id);
    this.processing.enqueue(this.dictation, templates, 'reprocess');
    this.isRegenerating = true;
    this.updateEditorDisabledState();
  }

  async retryProcessing() {
    if (!this.dictation) return;
    const doc = this.selectedDocument;
    if (!doc) return;

    // Stop recording if currently active since retrying will replace content
    await this.stopRecordingIfActive();
    this.activeRecordingDocumentKey = null;
    this.isTranscriptionStreaming = false;

    const workflowTriggerEnabled = this.isWorkflowTriggerEnabled(doc.templateId, doc.templateType);
    const template: Template = {
      id: doc.templateId,
      type: doc.templateType,
      title: doc.templateTitle,
      workflow_trigger: workflowTriggerEnabled
    };
    this.processing.enqueue(this.dictation, [template], 'reprocess');
    this.regenerate.clearError(doc, this.dictation._id);
    // mark as regenerating immediately
    this.isRegenerating = true;
    this.updateEditorDisabledState();
  }

  onRegenerateErrorDismiss(): void {
    if (!this.dictation) return;
    this.regenerate.clearError(this.selectedDocument, this.dictation._id);
    this.cdr.detectChanges();
  }

  // Priority selector helpers
  get selectedDocument(): CopilotDocument | null {
    if (!this.hasDocuments) return null;
    return this.processedDocuments[this.selectedTemplateIndex] ?? null;
  }

  get recipientFieldsEnabled(): boolean {
    const doc = this.selectedDocument;
    return this.canManageRecipients(doc) && !this.isRecipientEditBlocked(doc);
  }

  get visibleRecipients(): CopilotRecipients | undefined {
    const doc = this.selectedDocument;
    if (!doc || !this.canManageRecipients(doc)) return undefined;
    return doc.recipients;
  }

  get recipientFieldsReadOnly(): boolean {
    return !!this.selectedDocument?.approved;
  }

  private trackRecipientFieldsDisabledViewed(): void {
    const doc = this.selectedDocument;
    const key = this.getDocumentKey(doc);
    if (!doc || !key || !this.dictation?._id || this.canManageRecipients(doc) || this.recipientDisabledViewed.has(key)) return;
    this.recipientDisabledViewed.add(key);
    this.analytics.track('recipient_fields_disabled_viewed', {
      surface: 'web_document',
      blueprint_id: String(doc.templateId),
      template_type: doc.templateType === 'user'
        ? 'personal'
        : doc.templateType === 'blueprint'
          ? 'ai_blueprint'
          : 'organisation',
    });
  }

  private canManageRecipients(doc?: CopilotDocument | null): boolean {
    if (!doc) return false;
    return isRecipientManagementEnabled(doc, {
      enableDictate: this.user.entity.settings.enableDictate === true,
      workflowTrigger: this.isWorkflowTriggerEnabled(doc.templateId, doc.templateType),
    });
  }

  get isFreeTextRecipientDisabled(): boolean {
    return this.user?.entity?.settings?.disableFreeTextRecipient === true;
  }

  private documentHasFreeTextRecipients(doc: CopilotDocument): boolean {
    const recipients = doc.recipients;
    if (!recipients) return false;
    return isManualRecipient(recipients.primaryRecipient)
      || ccRecipients(recipients).some((recipient) => isManualRecipient(recipient));
  }

  /** Free-text recipients are removed before approval so the approved snapshot does not include them. */
  private async removeFreeTextRecipientsBeforeApprove(doc: CopilotDocument): Promise<void> {
    if (!this.isFreeTextRecipientDisabled || !this.documentHasFreeTextRecipients(doc)) return;
    await this.removeFreeTextRecipients();
  }

  private async removeFreeTextRecipientsFromDocument(doc: CopilotDocument): Promise<void> {
    if (doc.approved || doc.recipients === undefined || this.isRecipientEditBlocked(doc)) return;
    const next = withoutManualRecipients(doc.recipients);
    if (!next) return;
    await this.persistDocumentRecipients(doc, next);
  }

  private async removeFreeTextRecipients(): Promise<void> {
    for (const doc of this.processedDocuments) {
      await this.removeFreeTextRecipientsFromDocument(doc);
    }
  }

  /** Job completion replaces the document, including recipients captured at job start. */
  private isRecipientEditBlocked(doc?: CopilotDocument | null): boolean {
    return !!doc && this.isDocumentGenerating(doc);
  }

  async openRecipientEditor(mode: 'primary' | 'cc', viewOnly = false): Promise<void> {
    const doc = this.selectedDocument;
    if (!doc || !this.canManageRecipients(doc) || this.isRecipientEditBlocked(doc)) return;
    const isViewOnly = viewOnly || !!doc.approved;
    const recipients = doc.recipients ?? { primaryRecipient: null, ccRecipients: [] };
    if (isViewOnly) {
      if (mode === 'primary' && !hasPrimaryRecipient(recipients)) return;
      if (mode === 'cc' && ccRecipients(recipients).length === 0) return;
    }

    this.analytics.track('recipient_selector_opened', {
      surface: 'web_document',
      blueprint_id: String(doc.templateId),
      entry_point: mode === 'primary' ? 'to_field' : 'cc_field',
      has_existing_recipients: mode === 'primary'
        ? hasPrimaryRecipient(recipients)
        : ccRecipients(recipients).length > 0,
    });
    const ref = this.dialog.open(RecipientDialogComponent, {
      size: 'md',
      presentation: 'dialog',
      tone: 'container-low',
      data: {
        mode,
        recipients,
        viewOnly: isViewOnly,
        dictationId: this.dictation?._id,
        templateId: doc.templateId,
        disableFreeText: this.isFreeTextRecipientDisabled,
      },
    });
    const result = await firstValueFrom(ref.afterClosed()) as CopilotRecipients | undefined;
    if (!result || isViewOnly) {
      this.analytics.track('recipient_selector_dismissed', {
        surface: 'web_document',
        blueprint_id: String(doc.templateId),
        had_unsaved_changes: false,
      });
      return;
    }
    const saved = await this.persistDocumentRecipients(doc, result);
    if (saved) {
      this.analytics.track('recipients_saved', {
        surface: 'web_document',
        blueprint_id: String(doc.templateId),
        cc_count: ccRecipients(result).length,
        primary_changed: (recipients.primaryRecipient ? recipientKey(recipients.primaryRecipient) : '')
          !== (result.primaryRecipient ? recipientKey(result.primaryRecipient) : ''),
      });
      this.openRecipientSnackBar('Recipients saved successfully');
    }
  }

  async removePrimaryRecipient(): Promise<void> {
    const doc = this.selectedDocument;
    if (!doc || !this.canManageRecipients(doc) || doc.approved || this.isRecipientEditBlocked(doc)) return;
    const removed = doc.recipients?.primaryRecipient;
    const next = cloneRecipients(doc.recipients);
    next.primaryRecipient = null;
    const saved = await this.persistRecipientRemoval(doc, next);
    if (!saved) return;
    this.analytics.track('recipient_removed_from_field', {
      surface: 'web_document',
      blueprint_id: String(doc.templateId),
      recipient_type: 'primary',
      recipient_source: isManualRecipient(removed) ? 'free_text' : 'default',
    });
  }

  async removeCcRecipient(): Promise<void> {
    const doc = this.selectedDocument;
    if (!doc || !this.canManageRecipients(doc) || doc.approved || this.isRecipientEditBlocked(doc)) return;
    const removed = ccRecipients(doc.recipients)[0];
    const next = cloneRecipients(doc.recipients);
    next.ccRecipients = ccRecipients(next).slice(1);
    const saved = await this.persistRecipientRemoval(doc, next);
    if (!saved) return;
    this.analytics.track('recipient_removed_from_field', {
      surface: 'web_document',
      blueprint_id: String(doc.templateId),
      recipient_type: 'cc',
      recipient_source: isManualRecipient(removed) ? 'free_text' : 'default',
    });
  }

  private async persistRecipientRemoval(doc: CopilotDocument, recipients: CopilotRecipients): Promise<boolean> {
    const saved = await this.persistDocumentRecipients(doc, recipients, 'Failed to remove recipient');
    if (saved) {
      this.openRecipientSnackBar('Recipient removed successfully');
    }
    return saved;
  }

  private openRecipientSnackBar(messageKey: string): void {
    this.snackBar.open(this.translate.instant(messageKey), this.translate.instant('Close'), {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'bottom',
    });
  }

  private processedDocumentPatch(doc: CopilotDocument): Record<string, unknown> {
    return {
      templateId: doc.templateId,
      templateTitle: doc.templateTitle,
      templateType: doc.templateType,
      position: doc.position,
      ...(doc.recipients !== undefined ? { recipients: doc.recipients } : {}),
    };
  }

  private async persistDocumentRecipients(
    doc: CopilotDocument,
    recipients: CopilotRecipients,
    failureMessage = 'Failed to save recipients',
  ): Promise<boolean> {
    if (!this.dictation || this.isRecipientEditBlocked(doc)) return false;
    const previousPrimary = doc.recipients?.primaryRecipient;
    recipients.primaryRecipient = nullAbsentRecipientFields(previousPrimary, recipients.primaryRecipient) ?? null;
    if (!hasPrimaryRecipient(recipients)) {
      recipients.recipientSalutation = null;
    } else {
      recipients.recipientSalutation = generateRecipientSalutation(recipients.primaryRecipient);
    }
    doc.recipients = recipients;
    const dictationDoc = this.dictation.copilot?.documents?.processed?.find(
      (item) => item.templateId === doc.templateId && item.templateType === doc.templateType
    );
    if (dictationDoc) {
      dictationDoc.recipients = recipients;
    }
    try {
      this.beginSaving();
      await this.apiService.updateDictationCopilot(this.dictation._id, {
        copilot: {
          documents: {
            processed: [
              {
                ...this.processedDocumentPatch(doc),
                sections: {},
              },
            ],
          },
        },
      });
      return true;
    } catch (e) {
      this.openRecipientSnackBar(failureMessage);
      return false;
    } finally {
      this.endSaving();
    }
  }

  get showPrioritySelector(): boolean {
    const doc = this.selectedDocument;
    return !!doc && this.isWorkflowTriggerEnabled(doc.templateId, doc.templateType);
  }

  get priorityLabel(): string {
    const p = this.selectedDocument?.priority ?? 1;
    if (p === 3) return 'High';
    if (p === 2) return 'Medium';
    return 'Low';
  }

  getDotColor(slot: 1 | 2 | 3): string {
    const p = this.selectedDocument?.priority ?? 1;
    // colours: low -> green + 2 grey; medium -> 2 amber + 1 grey; high -> 3 red
    const gray = '#C7CAD1';
    const green = '#53DD6C';
    const amber = '#E59F2A';
    const red = '#BA1A1A';
    if (p === 1) return slot === 1 ? green : gray;
    if (p === 2) return slot <= 2 ? amber : gray;
    return red;
  }

  async setPriority(priority: 1 | 2 | 3): Promise<void> {
    if (this.isRegenerating) return;
    const doc = this.selectedDocument;
    if (!this.dictation || !doc) return;
    const previousPriority = doc.priority ?? 1;

    const updated: CopilotDocument = { ...doc, priority };
    try {
      await this.apiService.updateDictationCopilot(this.dictation._id, {
        copilot: {
          documents: {
            processed: [
              {
                ...this.processedDocumentPatch(updated),
                sections: {},
                priority: updated.priority
              }
            ]
          }
        }
      });
      this.processedDocuments[this.selectedTemplateIndex].priority = updated.priority;
      if (previousPriority !== priority) {
        this.analytics.track('priority_changed', {
          encounter_id: this.dictation._id,
          surface: 'document_screen',
        });
      }
    } catch (e) {
      this.snackBar.open(this.translate.instant('Failed to update priority'), this.translate.instant('Close'), {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom'
      });
    }
  }

  // Editor toolbar actions
  toggleBold() {
    if (this.editor) {
      this.editor.chain().focus().toggleBold().run();
    }
  }

  toggleItalic() {
    if (this.editor) {
      this.editor.chain().focus().toggleItalic().run();
    }
  }

  toggleUnderline() {
    if (this.editor) {
      this.editor.chain().focus().toggleUnderline().run();
    }
  }

  toggleBulletList() {
    if (this.editor) {
      this.editor.chain().focus().toggleBulletList().run();
    }
  }

  toggleOrderedList() {
    if (this.editor) {
      this.editor.chain().focus().toggleOrderedList().run();
    }
  }

  // Document scoring
  scoreDocument(score: 'up' | 'down') {
    const doc = this.selectedDocument;
    if (!this.dictation || !doc) return;

    const currentRate = doc.rate ?? 0;
    const desired = score === 'up' ? 1 : -1;
    const newRate = currentRate === desired ? 0 : desired;

    // Optimistic UI update - update processedDocuments array
    this.processedDocuments[this.selectedTemplateIndex].rate = newRate;

    // Also update the dictation object to keep data in sync
    if (this.dictation.copilot?.documents?.processed) {
      const processedDocs = this.dictation.copilot.documents.processed;
      const dictationDocIndex = processedDocs.findIndex(
        d => d.templateId === doc.templateId && d.templateType === doc.templateType
      );
      if (dictationDocIndex >= 0) {
        processedDocs[dictationDocIndex].rate = newRate;
      }
    }

    // Trigger change detection to update the UI
    this.cdr.markForCheck();

    this.apiService.updateDictationCopilot(this.dictation._id, {
      copilot: {
        documents: {
          processed: [
            {
              ...this.processedDocumentPatch(doc),
              sections: {},
              rate: newRate
            }
          ]
        }
      }
    }).then(() => {
      if (newRate !== 0) {
        this.snackBar.open(this.translate.instant('Thank you for your feedback!'), this.translate.instant('Close'), {
          duration: 2000,
          horizontalPosition: 'right',
          verticalPosition: 'bottom'
        });
      }
    }).catch(() => {
      // Revert on failure
      const revertedRate = currentRate;
      this.processedDocuments[this.selectedTemplateIndex].rate = revertedRate;

      // Only revert dictation state if dictation still exists
      if (this.dictation) {
        const copilot = this.dictation.copilot;
        const documents = copilot?.documents;
        if (documents?.processed) {
          const processedDocs = documents.processed;
          const dictationDocIndex = processedDocs.findIndex(
            d => d.templateId === doc.templateId && d.templateType === doc.templateType
          );
          if (dictationDocIndex >= 0) {
            processedDocs[dictationDocIndex].rate = revertedRate;
          }
        }
      }

      this.cdr.markForCheck();
      this.snackBar.open(this.translate.instant('Failed to update rating'), this.translate.instant('Close'), {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom'
      });
    });
  }



  toggleRightPane(): void {
    this.rightPaneOpen = !this.rightPaneOpen;
  }

  get showResumeConsultation(): boolean {
    return isResumeEligible(this.dictation, this.flags.isEnabled(RESUME_CONSULTATION_FLAG))
      || this.isSmartDictationActive;
  }

  get retention(): DictationRetention {
    return getDictationRetention(this.dictation);
  }

  get resumeDisabledReason(): string | null {
    return this.isSmartDictationActive? this.disabledForSmartDictationTooltip : this.retention.resumeDisabledReason;
  }

  get isResumeConsultationDisabled(): boolean {
    const dictationId = this.dictation?._id;
    if (!dictationId) return false;
    if (!this.retention.canRecord || this.isSmartDictationActive) return true;
    return (this.currentJobs || []).some(
      (job: any) =>
        job?.dictationId === dictationId &&
        (job?.status === 'queued' || job?.status === 'running'),
    );
  }

  get showDocumentSyncToolbar(): boolean {
    if (!this.flags.isEnabled(RESUME_CONSULTATION_FLAG)) {
      return false;
    }
    if (!this.dictation || !this.selectedDocument) {
      return false;
    }
    if (this.selectedDocument.approved) {
      return false;
    }
    return this.freshness.isStale(this.dictation, this.selectedDocument);
  }

  get documentSyncUpdatedLabel(): string {
    return this.freshness.formatUpdatedLabel(this.selectedDocument);
  }

  get canSyncSelectedDocument(): boolean {
    const doc = this.selectedDocument;
    if (!doc || doc.approved || !this.retention.canGenerate || this.isSmartDictationActive) {
      return false;
    }
    return !this.isDocumentGenerating(doc);
  }

  get isSelectedDocumentSyncing(): boolean {
    const doc = this.selectedDocument!;
    const job = this.getActiveJob(this.dictation!._id, doc.templateId, doc.templateType);
    return job?.action === 'update';
  }

  async onSyncSelectedDocument(): Promise<void> {
    if (!this.canSyncSelectedDocument) {
      return;
    }

    const confirmed = await this.documentSync.confirmSync();
    if (!confirmed) {
      return;
    }

    this.analytics.track('sync_clicked', {
      surface: 'document_toolbar',
      was_suggested: this.isSelectedDocumentOutOfSync(),
      ...(this.dictation?._id ? { encounter_id: this.dictation._id } : {}),
    });

    await this.stopRecordingIfActive();
    this.activeRecordingDocumentKey = null;
    this.isTranscriptionStreaming = false;

    this.documentSync.sync(this.dictation!, this.selectedDocument!);
    this.updateEditorDisabledState();
  }

  private isSelectedDocumentOutOfSync(): boolean {
    const document = this.selectedDocument;
    if (!this.dictation || !document || document.approved) {
      return false;
    }
    return this.freshness.isStale(this.dictation, document);
  }

  get patientDisplayName(): string {
    const formattedName = formatDictationSubjectName(this.dictation?.copilot);
    return formattedName || this.translate.instant('No name');
  }

  get patientId(): string {
    return this.dictation?.copilot?.subject_id ? `ID ${this.dictation?.copilot?.subject_id}` : '';
  }

  get encounterDate(): string {
    if (this.dictation?.createdAt) {
      const d = new Date(this.dictation.createdAt);
      const hours = d.getHours().toString().padStart(2, '0');
      const minutes = d.getMinutes().toString().padStart(2, '0');
      return `${formatDmyDate(d)}, ${hours}:${minutes}`;
    }
    return '';
  }

  get consultationLocation(): string {
    if (!this.flags.isEnabled('visitSelection')) return '';
    return this.dictation?.copilot?.visit_location?.trim() || '';
  }

  async goBack() {
    await this.flushPendingUpdate();
    this.router.navigate(['/encounters']);
  }

  onBreadcrumbHome(): void {
    this.analytics.track('breadcrumb_clicked', { from_page: 'document', to_page: 'home' });
    void this.goHome();
  }

  onBreadcrumbEncounters(): void {
    this.analytics.track('breadcrumb_clicked', { from_page: 'document', to_page: 'encounters' });
    void this.goToEncounters();
  }

  async goHome(): Promise<void> {
    await this.flushPendingUpdate();
    this.router.navigate(['/home']);
  }

  async goToEncounters(): Promise<void> {
    await this.flushPendingUpdate();
    const queryParams = this.encountersPage !== null ? { page: this.encountersPage } : {};
    this.router.navigate(['/encounters'], { queryParams });
  }

  async goToEncounter(): Promise<void> {
    await this.flushPendingUpdate();
    const id = this.dictation?._id;
    if (id) {
      const statusBefore = this.dictation?.copilot?.status;
      this.analytics.track('encounter_resumed', {
        encounter_id: id,
        status_before: statusBefore === 'in_progress' || statusBefore === 'done' ? statusBefore : 'draft',
      });
      this.router.navigate(['/encounter', id], {
        state: { resumeSession: true },
      });
    } else {
      this.router.navigate(['/encounters']);
    }
  }

  private notifyWhenUpdateJobsComplete(jobs: any[]): void {
    const dictationId = this.dictation?._id;
    if (!dictationId) return;

    const activeUpdateJobs = jobs.filter(
      (job) =>
        job.dictationId === dictationId &&
        job.action === 'update' &&
        (job.status === 'queued' || job.status === 'running'),
    );

    if (activeUpdateJobs.length > 0) {
      this.updateJobsInFlight = true;
      this.activeUpdateJobCount = Math.max(this.activeUpdateJobCount, activeUpdateJobs.length);
      return;
    }

    if (this.updateJobsInFlight) {
      this.updateJobsInFlight = false;
      const hadFailure = jobs.some(
        (job) => job.dictationId === dictationId && job.action === 'update' && job.status === 'failed',
      );
      const toastKey = this.activeUpdateJobCount <= 1
        ? 'Document updated'
        : 'All documents have been regenerated';
      this.activeUpdateJobCount = 0;
      if (hadFailure) {
        return;
      }
      openCopilotSnackbar(this.snackBar, this.translate.instant(toastKey));
    }
  }

  openPatientEditor(): void {
    const data: any = {
      dictationId: this.dictation?._id || '',
      mode: this.dictation?.copilot?.type === 'dictation' ? 'dictation' : 'consultation',
      flow: 'document',
      promptRegenerateAfterEdit: this.shouldPromptRegenerateAfterEdit,
    };

    if (this.dictation?.copilot) {
      const copilot = this.dictation.copilot;
      // Forward any manually-entered patient field (name, id, or DOB) so partial
      // manual patients (e.g. DOB-only) round-trip into the edit dialog.
      const hasPatientData = !!(
        copilot.subject_name ||
        copilot.subject_first_name ||
        copilot.subject_last_name ||
        copilot.subject_id ||
        copilot.subject_dob
      );

      if (hasPatientData) {
        data.patient = {
          subject_name: copilot.subject_name || '',
          subject_prefix: copilot.subject_prefix || '',
          subject_first_name: copilot.subject_first_name || '',
          subject_middle_name: copilot.subject_middle_name || '',
          subject_last_name: copilot.subject_last_name || '',
          subject_id: copilot.subject_id || '',
          subject_alternative_id: copilot.subject_alternative_id || '',
          subject_dob: copilot.subject_dob || null,
          facility_id: copilot.facility_id ?? null,
        };
      }
    }
    let dialogRef = null;
    if (this.user.entity.settings.enableDictate === true) {
      dialogRef = this.dialog.open(EditPatientComponent, {
        size: 'lg',
        maxHeight: '100vh',
        data,
      });
    } else {
      const combinedName = formatDictationSubjectName(this.dictation?.copilot);
      dialogRef = this.dialog.open(ManualPatientSelectorComponent, {
        size: 'sm',
        data: {
          from: 'edit',
          dictationId: this.dictation?._id || '',
          patientName: combinedName,
          patientId: this.dictation?.copilot?.subject_id || '',
          dateOfBirth: this.dictation?.copilot?.subject_dob || null,
          flow: 'document',
          promptRegenerateAfterEdit: this.shouldPromptRegenerateAfterEdit,
        },
      });
    }
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.saved && result?.patient && this.dictation?.copilot) {
        Object.assign(this.dictation.copilot, result.patient);
        if (result.regenerateAfterEdit === 'regenerate') {
          void this.regenerateAllDocuments({ skipConfirmation: true });
        }
      }
    });
  }

  private updateEditorDisabledState(): void {
    const voiceSelectionLocked = this.isManualEditingLockedForVoiceSelection();
    if (!this.dictation) return;
    const selectedDoc = this.processedDocuments[this.selectedTemplateIndex];
    if (!selectedDoc) {
      this.isRegenerating = false;
      this.editor?.setEditable(!voiceSelectionLocked);
      return;
    }

    const hasRunningJob = this.isJobActive(this.dictation!._id, selectedDoc.templateId, selectedDoc.templateType);
    const isApproved = !!selectedDoc.approved;

    this.isRegenerating = this.isDocumentGenerating(selectedDoc);

    if (this.editor) {
      this.editor.setEditable(!(hasRunningJob || isApproved || voiceSelectionLocked));
    }
  }

  private isManualEditingLockedForVoiceSelection(): boolean {
    if (!this.isVoiceSelectionModeActive || !this.voiceSelectionSearchTerm || !this.editor) {
      return false;
    }
    const storage = (this.editor as any).storage?.searchAndReplace;
    const activeTerm = String(storage?.searchTerm ?? '').trim().toLowerCase();
    const expectedTerm = this.voiceSelectionSearchTerm.trim().toLowerCase();
    const results = Array.isArray(storage?.results) ? storage.results : [];
    return !!activeTerm && activeTerm === expectedTerm && results.length > 0;
  }

  private normalizeCommandName(commandName: string): string {
    return commandName.trim().toLowerCase();
  }

  private isVoiceSelectionWorkflowCommand(commandName: string): boolean {
    return commandName === 'select-<text>' ||
      commandName === '[choose-<number>]' ||
      commandName === 'previous-match' ||
      commandName === '[next-match]' ||
      commandName === 'next-match' ||
      commandName === 'unselect-text';
  }

  private isVoiceSelectionFollowUpCommand(commandName: string): boolean {
    return commandName === 'bold-that' ||
      commandName === 'italicise-that' ||
      commandName === 'underline-that' ||
      commandName === 'uppercase-that' ||
      commandName === 'lowercase-that' ||
      commandName === 'capitalise-that';
  }

  private async enterVoiceSelectionMode(command: FrontendPhrase): Promise<void> {
    if (this.isVoiceSelectionModeActive && this.editor) {
      this.textService.clearSelectTextMode(this.editor, this.voiceSelectionSearchTerm);
    }
    const termRaw = Array.isArray(command.parameters) && command.parameters.length > 0
      ? String(command.parameters[0] ?? '')
      : '';
    this.voiceSelectionSearchTerm = termRaw.trim();
    if (!this.voiceSelectionSearchTerm) {
      return;
    }
    await this.flushPendingUpdate();
    this.isVoiceSelectionModeActive = true;
    this.updateEditorDisabledState();
  }

  private async exitVoiceSelectionMode(removePrefixes = true): Promise<void> {
    if (!this.isVoiceSelectionModeActive) return;
    this.isVoiceSelectionModeActive = false;
    if (removePrefixes && this.editor) {
      this.textService.clearSelectTextMode(this.editor, this.voiceSelectionSearchTerm);
    }
    this.voiceSelectionSearchTerm = '';
    this.updateEditorDisabledState();
  }

  // Helpers to reflect generating status on tabs
  isDocumentGenerating(doc: CopilotDocument): boolean {
    if (!this.dictation) return false;
    const isSmartTarget =
      this.isSmartProcessing
      && !!this.smartProcessingDocumentKey
      && this.getDocumentKey(doc) === this.smartProcessingDocumentKey;
    return isSmartTarget || this.isJobActive(this.dictation._id, doc.templateId, doc.templateType);
  }

  getTabLabel(doc: CopilotDocument): string {
    return this.isDocumentGenerating(doc) ? `${doc.templateTitle} (Generating)` : doc.templateTitle;
  }

  // ==== Reusable helpers ====
  private setEditorContentFromIndex(index: number): void {
    const doc = this.processedDocuments[index];
    const content = this.buildContentFromDoc(doc);
    this.editor!.commands.setContent(content, { emitUpdate: false });
    this.resetEditorHistory();

    // Get the HTML and apply transformations to track the initial content as "saved"
    let html = this.editor!.getHTML();
    this.programmaticHtmlToIgnore = html;


    // Track the initial content as "saved" to avoid unnecessary saves
    if (doc) {
      const docKey = `${doc.templateId}_${doc.templateType}`;
      this.lastSavedContent.set(docKey, html);
    }
  }

  /**
   * Every template tab shares one editor instance, so it also shares one undo stack.
   * Dropping the stack after each content swap stops undo from replaying another
   * document's steps into the active one (TDP-2752). Must run after setContent so the
   * swap itself is discarded too.
   */
  private resetEditorHistory(): void {
    if (!this.editor) return;
    this.editor.unregisterPlugin('history');
    this.editor.registerPlugin(createHistoryPlugin(EDITOR_HISTORY_OPTIONS));
  }

  private ensureCopilotDocuments(): { original: CopilotDocument[]; processed: CopilotDocument[] } {
    if (!this.dictation) {
      throw new Error(this.translate.instant('Dictation not loaded'));
    }
    if (!this.dictation.copilot) {
      this.dictation.copilot = { documents: { original: [], processed: [] }, subject_name: '', subject_id: '', subject_visit_id: '', subject_alternative_id: '', subject_dob: '', metrics: { totalOriginal: 0, totalProcessed: 0 } } as any;
    }
    const docs = this.dictation.copilot!.documents;
    docs.original = (docs.original || []) as CopilotDocument[];
    docs.processed = (docs.processed || []) as CopilotDocument[];
    return docs;
  }

  private upsertDocument(list: CopilotDocument[], item: CopilotDocument): void {
    // Skip upserting if the incoming document is marked for deletion
    if (item._delete) return;

    const idx = list.findIndex(d => d.templateId === item.templateId && d.templateType === item.templateType);
    if (idx >= 0) list[idx] = item; else list.push(item);
  }

  private isJobActive(dictationId: string, templateId: number, templateType: string): boolean {
    return (this.currentJobs || []).some((j: any) => j?.dictationId === dictationId && j?.templateId === templateId && j?.templateType === templateType && (j?.status === 'queued' || j?.status === 'running'));
  }

  private getActiveJob(dictationId: string, templateId: number, templateType: string): any | null {
    return (this.currentJobs || []).find((j: any) => j?.dictationId === dictationId && j?.templateId === templateId && j?.templateType === templateType && (j?.status === 'queued' || j?.status === 'running')) || null;
  }

  get currentJobAction(): 'process' | 'reprocess' | 'update' | null {
    if (!this.dictation || !this.selectedDocument) return null;
    const job = this.getActiveJob(this.dictation._id, this.selectedDocument.templateId, this.selectedDocument.templateType);
    return job?.action ?? null;
  }

  get processingOverlayMessage(): string {
    switch (this.currentJobAction) {
      case 'reprocess':
        return 'Regenerating document ...';
      case 'update':
        return 'Updating document ...';
      default:
        return 'Generating document ...';
    }
  }

  protected handleDeleteEncounter() {
    const ref = this.dialog.open<ConfirmComponent, ConfirmDialogData, boolean>(ConfirmComponent, {
      data: {
        title: this.translate.instant('Delete encounter?'),
        warning: this.translate.instant('Deleting this encounter and all associated documents cannot be undone, are you sure you wish to proceed?'),
        cancelText: this.translate.instant('Cancel'),
        confirmText: this.translate.instant('Delete encounter')
      },
      presentation: 'dialog',
    });

    firstValueFrom(ref.afterClosed()).then(confirmed => {
      if (confirmed) {
        this.deleteEncounter();
      }
    });
  };

  async deleteEncounter() {
    if (!this.dictation) return;
    await this.flushPendingUpdate();
    const deletedEncounterId = this.dictation._id;
    this.apiService.deleteDictation(this.dictation._id).then(() => {
      this.analytics.track('encounter_deleted', {
        encounter_id: deletedEncounterId,
        encounter_type: toAnalyticsEncounterType(this.dictation?.copilot?.type),
        surface: 'document_screen',
      });
      this.snackBar.open(this.translate.instant('Encounter has been deleted'), this.translate.instant('Close'), {
        duration: 2000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom'
      });
      // Navigate based on where the user came from
      if (this.fromEncounters) {
        const queryParams = this.encountersPage !== null ? { page: this.encountersPage } : {};
        this.router.navigate(['/encounters'], { queryParams });
      } else {
        this.router.navigate(['/home']);
      }
    }).catch(() => undefined);
  }

  // === Patient helpers ===
  get patientAge(): number | null {
    return calculateAgeYears(this.dictation?.copilot?.subject_dob);
  }

  get patientDobLabel(): string {
    const dob = this.dictation?.copilot?.subject_dob;
    if (!dob) return '';
    const trimmed = dob.trim();
    let d: Date;
    const ymd = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
    const dmy = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (ymd) {
      d = new Date(parseInt(ymd[1], 10), parseInt(ymd[2], 10) - 1, parseInt(ymd[3], 10));
    } else if (dmy) {
      d = new Date(parseInt(dmy[3], 10), parseInt(dmy[2], 10) - 1, parseInt(dmy[1], 10));
    } else {
      d = new Date(trimmed);
    }
    if (isNaN(d.getTime())) return '';
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  // Check if dictation has at least one approved document
  hasApprovedDocuments(): boolean {
    if (!this.dictation?.copilot?.documents?.processed) {
      return false;
    }
    return this.dictation.copilot.documents.processed.some(doc => doc.approved === true);
  }

  onRecordControlClicked(action: RecordControlAction): void {
    const document = this.selectedDocument;
    this.analytics.track(
      action === 'start' ? 'start_dictation_clicked' : 'end_dictation_clicked',
      {
        encounter_id: this.dictation?._id,
        ...(document?.templateId != null ? { blueprint_id: String(document.templateId) } : {}),
        encounter_type: toAnalyticsEncounterType(this.dictation?.copilot?.type),
        surface: 'document_screen',
      },
    );
  }

  get showRecordButton(): boolean {
    return this.dictation?.type !== 'backend';
  }

  // Check if the record button should be disabled (when current document is approved or generating or disconnected or has error)
  get isRecordButtonDisabled(): boolean {
    const isApproved = !!this.selectedDocument?.approved;
    const hasError = !!this.selectedDocument?.error;
    //TODO move this logic to the record button component (record-button.component.ts)
    const isDisconnected = this.recordingState === RecordingState.Disconnected;
    const isGenerating = this.selectedDocument ? this.isDocumentGenerating(this.selectedDocument) : false;
    const noMicrophoneAccess = !this.hasMicrophoneAccess;

    const isCopilotJobGenerating = isGenerating && !this.isDocumentSmartProcessing(
      this.selectedDocument?.templateId ?? -1,
      this.selectedDocument?.templateType ?? '',
    );

    return isApproved || hasError || isDisconnected || isCopilotJobGenerating || noMicrophoneAccess
      || !this.retention.canRecord;
  }

  get dictationDisabledTooltip(): string | null {
    if (!this.isRecordButtonDisabled || !this.retention.audioDeleted) {
      return null;
    }
    return "Audio was removed under your organisation's data retention policy, dictating is not possible";
  }

  // Stop recording if currently active
  private async stopRecordingIfActive(): Promise<void> {
    try {
      if (this.recorderService.isConsultationRecording()) {
        await this.recorderService.pause();
      }
    } catch (error) {
      console.error('Error stopping recording:', error);
    }
  }

  private async waitForFinalUtterance(timeoutMs = 2000, signal?: AbortSignal): Promise<void> {
    if (signal?.aborted) return;
    if (!this.isTranscriptionStreaming && !this.waitingForFinalTranscript) {
      return;
    }

    const finalResult$ = this.recorderService.frontendResult$.pipe(
      filter((r): r is FrontendResult => r?.type === 'result' && !!r?.final),
      take(1),
    );

    const abort$ = signal
      ? fromEvent(signal, 'abort').pipe(map(() => null as FrontendResult | null), take(1))
      : EMPTY;

    const trailing = await firstValueFrom(race(
      finalResult$,
      timer(timeoutMs).pipe(map(() => null as FrontendResult | null)),
      abort$,
    ));

    if (signal?.aborted) return;

    if (trailing && this.isSmartDictationActive) {
      this.appendPendingSmartTranscript(trailing.words?.[0] || '');
    }
  }

}
